import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Circle, AlertCircle,
  FileText, Download, ChevronRight, Target, Edit2,
  Save, X, Unlock, Brain, Upload, Trash2, ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import SmartRouterBanner from '../components/SmartRouter/SmartRouterBanner';
import EstimateDisclaimer from '../components/EstimateDisclaimer';
import EngagementBridgePanel from '../components/BDAssistant/EngagementBridgePanel';
import { useAuth } from '../contexts/AuthContext';
import { useOrchestratorContext } from '../hooks/useOrchestratorContext';
import { useSmartRouterSessionContext } from '../hooks/useSmartRouterSessionContext';
import {
  syncOrchestratorFromEngagement,
  issueStepDeepLink,
  getOrchestratorSession,
  completeOrchestratorStep,
} from '../services/smartStartApi';

const SUB_STEP_TO_STAGE: Record<string, string> = {
  intake: 'intake',
  synthesis: 'synthesis',
  ou_oa: 'ou_oa',
  plan: 'plan',
  datasim: 'datasim',
  sow: 'sow',
  won: 'won',
};

const STAGE_TO_STEP_KEY: Record<string, string> = {
  intake: 'create_or_pick_engagement',
  synthesis: 'discovery_synthesis',
  ou_oa: 'ou_oa_deck',
  plan: 'project_plan',
  datasim: 'datasim_pressure_test',
  sow: 'sow_generator',
  won: 'pm_handoff',
};

interface Engagement {
  id: number;
  name: string;
  client_name: string | null;
  stage: string;
  budget_cap: number | null;
  margin_floor_override: number;
  notes: string | null;
  approach_notes: string | null;
  smart_start_session_id: number | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  owner?: { id: number; name: string; email: string };
  artifacts?: BDArtifact[];
}

interface BDArtifact {
  id: number;
  type: string;
  version: number;
  status: string;
  file_ref: string | null;
  content: Record<string, any>;
  metadata: Record<string, any>;
  prerequisites_bypassed: boolean;
  created_by_type?: 'user' | 'ai';
  created_at: string;
}

const STAGES = [
  { key: 'intake',    label: 'Document Intake',  description: 'Upload discovery docs, transcripts, RFPs' },
  { key: 'synthesis', label: 'Synthesis',         description: 'AI extracts pain points, stakeholders, opportunities' },
  { key: 'ou_oa',     label: 'OU/OA Deck',        description: 'Branded PPTX with tag-matched case studies' },
  { key: 'plan',      label: 'Project Plan',      description: 'Backwards planning + RateCard pricing → Excel' },
  { key: 'datasim',   label: 'DataSim',           description: 'Monte Carlo pressure test — margin + workload check' },
  { key: 'sow',       label: 'SOW Generator',     description: 'Word SOW + Summary PPTX — human review required' },
  { key: 'won',       label: 'PM Handoff',        description: 'Push to Project Management on deal win' },
];

const ARTIFACT_LABELS: Record<string, string> = {
  transcript: 'Transcript',
  source_doc: 'Source Document',
  synthesis: 'Synthesis Report',
  ou_oa_deck: 'OU/OA Deck',
  project_plan: 'Project Plan',
  datasim_run: 'DataSim Results',
  sow_doc: 'SOW Document',
  sow_deck: 'SOW Summary Deck',
  unknowns_research: 'Estimate Research',
};

const ARTIFACT_ICONS: Record<string, string> = {
  synthesis: '📋',
  unknowns_research: '🔍',
  ou_oa_deck: '📊',
  project_plan: '📈',
  datasim_run: '🎲',
  sow_doc: '📄',
  sow_deck: '🖼️',
  transcript: '🎤',
  source_doc: '📁',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  in_review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  superseded: 'bg-gray-100 text-gray-400',
};

const STAGE_ARTIFACT_TYPE: Record<string, string> = {
  synthesis: 'synthesis',
  ou_oa:     'ou_oa_deck',
  plan:      'project_plan',
  datasim:   'datasim_run',
  sow:       'sow_doc',
};

const getStageStatus = (stageKey: string, currentStage: string, artifacts: any[]) => {
  const order = STAGES.map(s => s.key);
  const si = order.indexOf(stageKey);
  const ci = order.indexOf(currentStage);
  if (si > ci) return 'upcoming';
  // Check artifact approval for both past AND current DB stage
  const requiredType = STAGE_ARTIFACT_TYPE[stageKey];
  if (requiredType) {
    const artifact = artifacts.find((a: any) => a.type === requiredType);
    if (!artifact || artifact.status !== 'approved') return 'skipped';
  }
  if (si === ci) return 'current';
  return 'completed';
};

const BDEngagementDetail: React.FC = () => {
  const { user } = useAuth();
  const canEditRoleAllocation = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orchestratorCtx = useOrchestratorContext();
  const routerSession = useSmartRouterSessionContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastSyncedStageRef = useRef<string | null>(null);
  const councilAutoFetchedRef = useRef(false);

  const [engagement, setEngagement] = useState<Engagement | null>(null);
  const [viewStage, setViewStage] = useState<string | null>(() => {
    const sub = orchestratorCtx.subStep;
    return sub && SUB_STEP_TO_STAGE[sub] ? SUB_STEP_TO_STAGE[sub] : null;
  });
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', client_name: '', notes: '', budget_cap: '' });
  const [saving, setSaving] = useState(false);

  // Intake stage state
  const [artifactType, setArtifactType] = useState<'transcript' | 'source_doc'>('source_doc');
  const [uploading, setUploading] = useState(false);
  const [uploadingDeliverable, setUploadingDeliverable] = useState(false);
  const deliverableInputRef = useRef<HTMLInputElement>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [approvingAndAdvancing, setApprovingAndAdvancing] = useState(false);
  const [approvingDeck, setApprovingDeck] = useState(false);
  const [approvingPlan, setApprovingPlan] = useState(false);
  const [approvingSim, setApprovingSim] = useState(false);
  const [approvingSow, setApprovingSow] = useState(false);

  // Synthesis stage state
  const [synthesizing, setSynthesizing] = useState(false);
  const [runningCouncil, setRunningCouncil] = useState(false);
  const [councilReview, setCouncilReview] = useState<any>(null);

  // OU/OA stage state
  const [generatingDeck, setGeneratingDeck] = useState(false);
  // Opt-in only — routes the deck through Anthropic's hosted pptx skill
  // (Claude designs and runs its own python-pptx code) instead of the
  // default Sonnet-outline + pptxgenjs pipeline. Costs meaningfully more per
  // generation, so it defaults off and is never auto-enabled.
  const [enhancedVisualization, setEnhancedVisualization] = useState(false);
  // Local draft of the user's "Our Approach" notes textarea. Synced from
  // the server on load; persisted via PATCH on blur or before regeneration.
  const [approachNotesDraft, setApproachNotesDraft] = useState<string>('');
  const [savingApproachNotes, setSavingApproachNotes] = useState(false);
  const lastSavedApproachNotesRef = useRef<string>('');

  // Project Plan stage state
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [capacityTeamSize, setCapacityTeamSize] = useState<number>(4);
  const [capacityWeeks, setCapacityWeeks] = useState<number>(8);
  const [capacityComposition, setCapacityComposition] = useState<Array<{ role: string; allocation_pct: number }>>([]);
  const [rateCardRoleNames, setRateCardRoleNames] = useState<string[]>([]);

  // DataSim stage state
  const [runningDataSim, setRunningDataSim] = useState(false);
  // Constraint assumptions. null means "whatever the engagement already has,
  // else the server defaults" — we only send a config once the user edits one,
  // so an untouched run keeps behaving exactly as it did before.
  const [datasimConstraints, setDatasimConstraints] = useState<any | null>(null);
  const [showAssumptions, setShowAssumptions] = useState(false);
  // Ranges the consultant has corrected by hand, keyed by the task key the
  // backend supplies on each row. Sent on the next run; the server merges them
  // into whatever is already saved on the engagement.
  const [rangeOverrides, setRangeOverrides] = useState<Record<string, any>>({});
  const [editingRange, setEditingRange] = useState<string | null>(null);
  const [researchingUnknowns, setResearchingUnknowns] = useState(false);
  const [fetchingCouncil, setFetchingCouncil] = useState(false);
  const [councilResult, setCouncilResult] = useState<{ summary: string; recommendations: any[] } | null>(null);
  const [acceptedRecs, setAcceptedRecs] = useState<Set<string>>(new Set());
  const [applyingRevision, setApplyingRevision] = useState(false);
  const [datasimOverrideModal, setDatasimOverrideModal] = useState<{ open: boolean; onConfirm: () => Promise<void> }>({ open: false, onConfirm: async () => {} });

  // SOW stage state
  const [generatingSow, setGeneratingSow] = useState(false);

  // PM Handoff state
  const [handingOff, setHandingOff] = useState(false);

  // Artifact panel loading states
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [approvingArtifactId, setApprovingArtifactId] = useState<number | null>(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadingStageFile, setDownloadingStageFile] = useState(false);
  const [handoffResult, setHandoffResult] = useState<{ pm_project_id: number; workstreams_created: number; tasks_created: number } | null>(null);

  // Pricing state. The four engagement-level settings from Ryan's workbook,
  // held as strings so a half-typed number does not become NaN mid-keystroke,
  // plus the per-role Flat/Cost overrides (the engine's rule 3).
  const [pricingForm, setPricingForm] = useState<{
    baselineCostPerWeek: string; additionalMarginPct: string; tePct: string; teOnOwnLine: boolean;
  } | null>(null);
  const [pricingOverrides, setPricingOverrides] = useState<Record<string, string>>({});
  const [savingPricing, setSavingPricing] = useState(false);
  const [downloadingPricingModel, setDownloadingPricingModel] = useState(false);

  const fetchEngagement = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/engagements/${id}`);
      setEngagement(res.data);
      const savedCapacity = res.data?.metadata?.team_capacity;
      if (savedCapacity && typeof savedCapacity === 'object') {
        if (typeof savedCapacity.team_size === 'number') setCapacityTeamSize(savedCapacity.team_size);
        if (typeof savedCapacity.engagement_weeks === 'number') setCapacityWeeks(savedCapacity.engagement_weeks);
        if (Array.isArray(savedCapacity.composition)) setCapacityComposition(savedCapacity.composition);
      }
      setEditForm({
        name: res.data.name,
        client_name: res.data.client_name || '',
        notes: res.data.notes || '',
        budget_cap: res.data.budget_cap ? String(res.data.budget_cap) : '',
      });
      // Restore council results from artifact content
      const artifacts = res.data.artifacts || [];
      const synthArtifact = artifacts.find((a: any) => a.type === 'synthesis');
      if (synthArtifact?.content?.council) setCouncilReview(synthArtifact.content.council);
      const simArtifact = artifacts.find((a: any) => a.type === 'datasim_run');
      if (simArtifact?.content?.council) {
        const council = simArtifact.content.council;
        setCouncilResult(council);
        setAcceptedRecs(new Set((council.recommendations || []).map((r: any) => r.id)));
      }
    } catch {
      toast.error('Failed to load engagement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Silent refresh — updates data without showing full-page spinner
  const refreshEngagement = useCallback(async () => {
    try {
      const res = await api.get(`/api/engagements/${id}`);
      setEngagement(res.data);
    } catch (err: any) {
      console.error('[refreshEngagement] failed:', err?.message);
    }
  }, [id]);

  useEffect(() => {
    if (!engagement) return;
    const serverValue = engagement.approach_notes || '';
    if (approachNotesDraft === lastSavedApproachNotesRef.current) {
      setApproachNotesDraft(serverValue);
      lastSavedApproachNotesRef.current = serverValue;
    }
  }, [engagement, approachNotesDraft]);

  const saveApproachNotes = useCallback(async () => {
    if (!engagement) return;
    const next = approachNotesDraft;
    if (next === lastSavedApproachNotesRef.current) return; // nothing changed
    try {
      setSavingApproachNotes(true);
      await api.patch(`/api/engagements/${engagement.id}`, { approach_notes: next });
      lastSavedApproachNotesRef.current = next;
      setEngagement(prev => prev ? { ...prev, approach_notes: next || null } : prev);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save approach notes');
    } finally {
      setSavingApproachNotes(false);
    }
  }, [engagement, approachNotesDraft]);

  // Auto-trigger AI Council when DataSim fails and no results cached yet
  useEffect(() => {
    if (councilAutoFetchedRef.current || !engagement || councilResult || fetchingCouncil) return;
    const simArtifact = (engagement.artifacts || []).find((a: any) => a.type === 'datasim_run');
    if (simArtifact?.content?.overall_pass === false && !simArtifact.content?.council) {
      councilAutoFetchedRef.current = true;
      fetchCouncilRecommendations();
    }
  }, [engagement]); // intentional: only re-run when engagement data changes

  useEffect(() => { fetchEngagement(); }, [fetchEngagement]);

  useEffect(() => {
    if (!canEditRoleAllocation) return;
    let cancelled = false;
    api.get('/api/company-brain/rate-card-roles')
      .then(res => {
        if (cancelled) return;
        const roles = Array.isArray(res.data?.roles) ? res.data.roles : [];
        const names = roles
          .map((r: any) => (typeof r?.roleName === 'string' ? r.roleName.trim() : ''))
          .filter((n: string) => n.length > 0);
        setRateCardRoleNames(Array.from(new Set(names)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canEditRoleAllocation]);

  useEffect(() => {
    if (!engagement) return;
    if (!orchestratorCtx.inRouter || orchestratorCtx.sessionId == null) return;
    if (lastSyncedStageRef.current === engagement.stage) return;
    lastSyncedStageRef.current = engagement.stage;

    const sessionId = orchestratorCtx.sessionId;
    const engagementStage = engagement.stage;
    const engagementId = engagement.id;

    (async () => {
      try {
        const session = await syncOrchestratorFromEngagement(sessionId, engagementId, engagementStage);
        const expectedStepKey = STAGE_TO_STEP_KEY[engagementStage];
        if (!expectedStepKey) return;
        const plan = (session as any).navigation_plan || (session as any).plan || [];
        const expectedStep = plan.find((p: any) => p.step_key === expectedStepKey);
        if (!expectedStep) return;

        const incoming = new URLSearchParams(window.location.search);
        const currentStepIndex = incoming.get('stepIndex');
        if (String(expectedStep.index) === currentStepIndex
          && incoming.get('stepKey') === expectedStep.step_key) {
          return;
        }

        const fresh = await issueStepDeepLink(sessionId, expectedStep.index);
        if (!fresh?.token) return;

        incoming.set('smartStartSessionId', String(sessionId));
        incoming.set('stepIndex', String(expectedStep.index));
        incoming.set('stepKey', expectedStep.step_key);
        if (expectedStep.sub_step) incoming.set('subStep', expectedStep.sub_step);
        else incoming.delete('subStep');
        incoming.set('orchestratorToken', fresh.token);
        navigate(`${window.location.pathname}?${incoming.toString()}`, { replace: true });
      } catch (_e) {
        lastSyncedStageRef.current = null;
      }
    })();
  }, [engagement, orchestratorCtx.inRouter, orchestratorCtx.sessionId, navigate]);

  const saveEdit = async () => {
    if (!engagement) return;
    try {
      setSaving(true);
      const res = await api.patch(`/api/engagements/${engagement.id}`, {
        name: editForm.name,
        client_name: editForm.client_name || null,
        notes: editForm.notes || null,
        budget_cap: editForm.budget_cap ? parseFloat(editForm.budget_cap) : null,
      });
      setEngagement(prev => prev ? { ...prev, ...res.data } : prev);
      setEditMode(false);
      toast.success('Updated');
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const updateArtifactStatus = async (artifactId: number, status: string) => {
    try {
      setApprovingArtifactId(artifactId);
      await api.patch(`/api/engagements/${id}/artifacts/${artifactId}`, { status });
      await refreshEngagement();
      toast.success(`Artifact ${status}`);
    } catch {
      toast.error('Failed to update artifact');
    } finally {
      setApprovingArtifactId(null);
    }
  };

  const goToStage = (stageKey: string) => {
    setViewStage(stageKey);
  };

  const approveSow = async (artifactId: number) => {
    try {
      setApprovingSow(true);
      await api.patch(`/api/engagements/${id}/artifacts/${artifactId}`, { status: 'approved' });
      await refreshEngagement();
      toast.success('SOW approved');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to approve SOW');
    } finally {
      setApprovingSow(false);
    }
  };

  const approveSimAndProceed = async (artifactId: number) => {
    const simArtifact = (engagement?.artifacts || []).find((a: any) => a.id === artifactId);
    const simPassed = simArtifact?.content?.overall_pass === true;

    if (!simPassed) {
      // Show modal BEFORE approving — artifact stays untouched if user cancels
      setDatasimOverrideModal({
        open: true,
        onConfirm: async () => {
          setApprovingSim(true);
          try {
            await api.patch(`/api/engagements/${id}/artifacts/${artifactId}`, { status: 'approved' });
            const res = await api.post(`/api/engagements/${id}/advance`, { datasim_override: true });
            setEngagement(prev => prev ? { ...prev, ...res.data } : prev);
            setViewStage(null);
            toast.success('DataSim overridden — moving to SOW');
          } catch (err: any) {
            toast.error(err?.response?.data?.error || 'Failed to proceed');
            await refreshEngagement();
          } finally {
            setApprovingSim(false);
          }
        },
      });
      return;
    }

    // Normal flow — datasim passed
    try {
      setApprovingSim(true);
      await api.patch(`/api/engagements/${id}/artifacts/${artifactId}`, { status: 'approved' });
      const res = await api.post(`/api/engagements/${id}/advance`);
      setEngagement(prev => prev ? { ...prev, ...res.data } : prev);
      setViewStage(null);
      toast.success('Results approved — moving to SOW');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to approve and proceed');
      await refreshEngagement();
    } finally {
      setApprovingSim(false);
    }
  };

  const approvePlanAndProceed = async (artifactId: number) => {
    try {
      setApprovingPlan(true);
      await api.patch(`/api/engagements/${id}/artifacts/${artifactId}`, { status: 'approved' });
      const res = await api.post(`/api/engagements/${id}/advance`);
      setEngagement(prev => prev ? { ...prev, ...res.data } : prev);
      setViewStage(null);
      toast.success('Plan approved — moving to DataSim');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to approve and proceed');
      await refreshEngagement();
    } finally {
      setApprovingPlan(false);
    }
  };

  const approveDeckAndProceed = async (artifactId: number) => {
    try {
      setApprovingDeck(true);
      await api.patch(`/api/engagements/${id}/artifacts/${artifactId}`, { status: 'approved' });
      const res = await api.post(`/api/engagements/${id}/advance`);
      setEngagement(prev => prev ? { ...prev, ...res.data } : prev);
      setViewStage(null);
      toast.success('Deck approved — moving to Project Plan');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to approve and proceed');
      await refreshEngagement();
    } finally {
      setApprovingDeck(false);
    }
  };

  const approveAndProceed = async (artifactId: number) => {
    try {
      setApprovingAndAdvancing(true);
      await api.patch(`/api/engagements/${id}/artifacts/${artifactId}`, { status: 'approved' });
      const res = await api.post(`/api/engagements/${id}/advance`);
      setEngagement(prev => prev ? { ...prev, ...res.data } : prev);
      setViewStage(null);
      toast.success('Synthesis approved — moving to OU/OA Deck');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to approve and proceed');
      await refreshEngagement();
    } finally {
      setApprovingAndAdvancing(false);
    }
  };

  const runAiCouncil = async () => {
    try {
      setRunningCouncil(true);
      const res = await api.post(`/api/engagements/${id}/run-ai-council`, undefined, {
        timeout: 900000, // 15 min — Sonnet review + rate-limit queue can exceed the 5-min default
      });
      setCouncilReview(res.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'AI Council review failed');
    } finally {
      setRunningCouncil(false);
    }
  };

  const deleteArtifact = async (artifactId: number) => {
    try {
      setDeletingId(artifactId);
      await api.delete(`/api/engagements/${id}/artifacts/${artifactId}`);
      await refreshEngagement();
      toast.success('File removed');
    } catch {
      toast.error('Failed to remove file');
    } finally {
      setDeletingId(null);
    }
  };

  const uploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      setUploading(true);
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append('files', f));
      formData.append('artifact_type', artifactType);
      await api.post(`/api/engagements/${id}/artifacts/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshEngagement();
      toast.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const uploadDeliverable = async (deliverableType: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      setUploadingDeliverable(true);
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('artifact_type', deliverableType);
      const res = await api.post(`/api/engagements/${id}/artifacts/upload-deliverable`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshEngagement();
      setViewStage(null);
      const skipped = (res.data?.bypassed || []) as string[];
      toast.success(
        skipped.length > 0
          ? `Uploaded — skipped ahead (${skipped.length} earlier step${skipped.length > 1 ? 's' : ''} bypassed)`
          : 'Deliverable uploaded'
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Deliverable upload failed');
    } finally {
      setUploadingDeliverable(false);
      if (deliverableInputRef.current) deliverableInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!routerSession.inRouter || routerSession.sessionId == null) return;
    if (routerSession.attachedDocuments.length === 0) return;
    if (!engagement?.id) return;
    const guardKey = `bd_router_docs_${routerSession.sessionId}_${engagement.id}`;
    if (localStorage.getItem(guardKey)) return;

    const docs = routerSession.attachedDocuments.filter(
      (d) => typeof d.text === 'string' && d.text.length > 0,
    );
    if (docs.length === 0) return;
    localStorage.setItem(guardKey, '1');

    (async () => {
      try {
        const formData = new FormData();
        docs.forEach((d) => {
          const fileName = `${d.name.replace(/\.[^.]+$/, '')}.txt`;
          formData.append('files', new File([new Blob([d.text], { type: 'text/plain' })], fileName, { type: 'text/plain' }));
        });
        formData.append('artifact_type', 'source_doc');
        await api.post(`/api/engagements/${engagement.id}/artifacts/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        await refreshEngagement();
        toast.success(`Loaded ${docs.length} document${docs.length === 1 ? '' : 's'} from Smart Router — no need to re-upload.`);
      } catch {
        localStorage.removeItem(guardKey);
      }
    })();
  }, [routerSession.inRouter, routerSession.sessionId, routerSession.attachedDocuments, engagement?.id, refreshEngagement]);

  const advanceStage = async () => {
    try {
      setAdvancing(true);
      try {
        const res = await api.post(`/api/engagements/${id}/advance`);
        setEngagement(prev => prev ? { ...prev, ...res.data } : prev);
        setViewStage(null);
        toast.success('Moved to Synthesis stage');
      } catch (advErr: any) {
        if (advErr?.response?.data?.datasim_failed) {
          setDatasimOverrideModal({
            open: true,
            onConfirm: async () => {
              const res = await api.post(`/api/engagements/${id}/advance`, { datasim_override: true });
              setEngagement(prev => prev ? { ...prev, ...res.data } : prev);
              setViewStage(null);
              toast.success('DataSim overridden — moving to SOW');
            },
          });
        } else {
          throw advErr;
        }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to advance stage');
    } finally {
      setAdvancing(false);
    }
  };

  const generateOuOa = async () => {
    try {
      setGeneratingDeck(true);
      // Flush any unsaved approach-notes typing into the body so the AI
      // sees the latest text on this run, even if the user clicked Generate
      // without blurring the textarea first.
      const originalTimeout = api.defaults.timeout;
      api.defaults.timeout = 1200000;
      try {
        await api.post(`/api/engagements/${id}/generate-ou-oa`, {
          approach_notes: approachNotesDraft,
          enhanced: enhancedVisualization,
        });
        lastSavedApproachNotesRef.current = approachNotesDraft;
      } finally {
        api.defaults.timeout = originalTimeout;
      }
      await refreshEngagement();
      toast.success('OU/OA Deck generated!');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Generation failed');
    } finally {
      setGeneratingDeck(false);
    }
  };

  const downloadArtifact = async (artifactId: number, fileName?: string) => {
    try {
      const response = await api.get(`/api/engagements/${id}/artifacts/${artifactId}/download`, { responseType: 'blob' });
      // x-file-name first: content-disposition is not CORS-readable, so on a
      // cross-origin response (which this always is) it comes back empty and
      // the file saved as "download" with no extension.
      const contentDisposition = response.headers['content-disposition'] || '';
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      const name = response.headers['x-file-name'] || match?.[1] || fileName || 'download';
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  // Re-price the existing plan with new settings/overrides. Deliberately not a
  // plan regeneration: trying a different baseline cost should change the
  // price, not hand the scope back to the model.
  const repricePlan = async (
    // null means "price it with whatever the rate card says" — used by the
    // first-time price of an old plan, where imposing engagement-level settings
    // would silently pin values the consultant never chose.
    form: { baselineCostPerWeek: string; additionalMarginPct: string; tePct: string; teOnOwnLine: boolean } | null,
    overrides: Record<string, string>,
    // The roles currently in the plan. An override typed against a role that a
    // later plan revision dropped would otherwise sit in engagement metadata
    // forever, applying to nothing and reappearing if the role came back.
    rolesInPlan: string[],
  ) => {
    try {
      setSavingPricing(true);
      // The API takes decimal fractions (0.05 for 5%) and rejects anything
      // above 1, so a percent typed here is converted here.
      const asPct = (v: string) => {
        const n = Number(v);
        return Number.isFinite(n) ? n / 100 : 0;
      };
      const asNum = (v: string) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };
      const res = await api.post(`/api/engagements/${id}/price-plan`, {
        ...(form ? {
          settings: {
            baselineCostPerWeek: asNum(form.baselineCostPerWeek),
            additionalMarginPct: asPct(form.additionalMarginPct),
            tePct: asPct(form.tePct),
            teOnOwnLine: form.teOnOwnLine,
          },
        } : {}),
        overrides: Object.entries(overrides)
          .filter(([role, v]) => role.trim() !== '' && String(v).trim() !== '' && rolesInPlan.includes(role))
          .map(([role, v]) => ({ role, override: Number(v) })),
      });
      const pricing = res.data?.pricing;
      if (pricing?.available) {
        toast.success(`Re-priced: $${Math.round(pricing.totals.totalSOW).toLocaleString()} at ${(pricing.margins.actualGrossMargin * 100).toFixed(1)}% margin`);
      } else {
        toast.error(pricing?.detail || 'Pricing not available');
      }
      await fetchEngagement();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Re-pricing failed');
    } finally {
      setSavingPricing(false);
    }
  };

  // The filled-in Excel pricing model, in Ryan's own workbook layout.
  const downloadPricingModel = async () => {
    try {
      setDownloadingPricingModel(true);
      const response = await api.get(`/api/engagements/${id}/pricing-workbook`, { responseType: 'blob' });
      const contentDisposition = response.headers['content-disposition'] || '';
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      // See downloadArtifact: x-file-name is the one the browser can read.
      a.download = response.headers['x-file-name'] || match?.[1] || 'Pricing_Model.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      // The route refuses rather than handing out a workbook of zeros, and the
      // reason is the actionable part — so it has to survive the blob response.
      let message = 'Pricing model download failed';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.error) message = parsed.error;
        } catch { /* not JSON — keep the generic message */ }
      } else if (data?.error) {
        message = data.error;
      }
      toast.error(message);
    } finally {
      setDownloadingPricingModel(false);
    }
  };

  const runPmHandoff = async () => {
    try {
      setHandingOff(true);
      const res = await api.post(`/api/engagements/${id}/pm-handoff`);
      setHandoffResult(res.data);
      toast.success('Pushed to Project Management!');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'PM handoff failed');
    } finally {
      setHandingOff(false);
    }
  };

  const generateSow = async () => {
    try {
      setGeneratingSow(true);
      await api.post(`/api/engagements/${id}/generate-sow`);
      await refreshEngagement();
      toast.success('SOW generated!');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'SOW generation failed');
    } finally {
      setGeneratingSow(false);
    }
  };

  const runDataSim = async () => {
    setCouncilResult(null);
    setAcceptedRecs(new Set());
    try {
      setRunningDataSim(true);
      const body: any = {};
      if (datasimConstraints) body.constraint_config = datasimConstraints;
      if (Object.keys(rangeOverrides).length > 0) body.range_overrides = rangeOverrides;
      await api.post(`/api/engagements/${id}/run-datasim`, body);
      await refreshEngagement();
      toast.success('DataSim complete!');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'DataSim failed');
    } finally {
      setRunningDataSim(false);
    }
  };

  // Look up external benchmarks for the estimates the planner flagged as
  // guesses. Explicitly user-triggered: each unknown costs a paid web search.
  const researchUnknowns = async () => {
    try {
      setResearchingUnknowns(true);
      const res = await api.post(`/api/engagements/${id}/research-unknowns`);
      if (Array.isArray(res.data?.findings) && res.data.findings.length === 0) {
        toast.success(res.data.message || 'No unknown estimates to research.');
      } else {
        toast.success('Research complete — check the sources before using any of it.');
      }
      await refreshEngagement();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Research failed');
    } finally {
      setResearchingUnknowns(false);
    }
  };

  const fetchCouncilRecommendations = async () => {
    try {
      setFetchingCouncil(true);
      const res = await api.post(`/api/engagements/${id}/datasim-council`);
      setCouncilResult(res.data);
      setAcceptedRecs(new Set(res.data.recommendations.map((r: any) => r.id)));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to get recommendations');
    } finally {
      setFetchingCouncil(false);
    }
  };

  const applyRevision = async () => {
    if (!councilResult) return;
    const accepted = councilResult.recommendations
      .filter(r => acceptedRecs.has(r.id))
      .map(r => `${r.title}: ${r.description}`);
    if (accepted.length === 0) { toast.error('Select at least one recommendation'); return; }
    try {
      setApplyingRevision(true);
      const res = await api.post(`/api/engagements/${id}/datasim-revision`, { acceptedRecommendations: accepted });
      setEngagement(res.data.engagement);
      setCouncilResult(null);
      setAcceptedRecs(new Set());
      councilAutoFetchedRef.current = false;
      toast.success('Plan revised — DataSim re-run complete!');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to apply revision');
    } finally {
      setApplyingRevision(false);
    }
  };

  const generateProjectPlan = async () => {
    try {
      setGeneratingPlan(true);
      const res = await api.post(`/api/engagements/${id}/generate-project-plan`, {
        team_capacity: {
          team_size: capacityTeamSize,
          engagement_weeks: capacityWeeks,
          composition: capacityComposition.filter(c => c.role && c.allocation_pct > 0)
        }
      });
      setEngagement(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          artifacts: [...(prev.artifacts || []).filter((a: any) => a.type !== 'project_plan'), res.data],
        };
      });
      toast.success('Project Plan generated!');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Generation failed');
    } finally {
      setGeneratingPlan(false);
    }
  };

  const runSynthesis = async () => {
    try {
      setSynthesizing(true);
      await api.post(`/api/engagements/${id}/synthesize`);
      await refreshEngagement();
      toast.success('Synthesis complete!');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Synthesis failed');
    } finally {
      setSynthesizing(false);
    }
  };

  const engagementRef = useRef<Engagement | null>(null);
  engagementRef.current = engagement;

  const buildFinishPayload = useCallback(async () => {
    const eng = engagementRef.current;
    if (!eng || orchestratorCtx.sessionId == null) {
      toast.error('Cannot finish — engagement or session missing.');
      return null;
    }
    const pmComplete = eng.stage === 'won' && !!eng.metadata?.pm_project_id;
    if (!pmComplete) {
      toast.error('Push the engagement to PM first.');
      return null;
    }

    const sessionId = orchestratorCtx.sessionId;
    try {
      const session = await getOrchestratorSession(sessionId);
      const plan = (session as any).navigation_plan || (session as any).plan || [];
      const pmStep = plan.find((p: any) => p.step_key === 'pm_handoff');
      if (!pmStep) {
        toast.error('PM Handoff step not found in plan.');
        return null;
      }

      if (pmStep.status !== 'completed') {
        const fresh = await issueStepDeepLink(sessionId, pmStep.index);
        if (!fresh?.token) {
          toast.error('Could not issue deep-link token for PM Handoff.');
          return null;
        }
        await completeOrchestratorStep(sessionId, pmStep.index, {
          token: fresh.token,
          artifact: {
            engagement_id: eng.id,
            stage: eng.stage,
            pm_project_id: eng.metadata?.pm_project_id ?? null,
          },
          summary: `BD plan complete — engagement #${eng.id} pushed to Project Management (#${eng.metadata?.pm_project_id ?? 'n/a'}).`,
        });
      }

      toast.success('Smart Router plan finished.');
      const target = orchestratorCtx.returnTo || `/app/smart-start/${sessionId}`;
      if (/^https?:\/\//i.test(target)) {
        window.location.assign(target);
      } else {
        navigate(target);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'unknown error';
      toast.error(`Could not finish: ${msg}`);
    }
    return null;
  }, [orchestratorCtx.sessionId, orchestratorCtx.returnTo, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!engagement) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8 text-center text-gray-500">
        Engagement not found.
      </div>
    );
  }

  const ARTIFACT_ORDER: Record<string, number> = {
    transcript: 0, source_doc: 1, synthesis: 2, ou_oa_deck: 3, project_plan: 4, datasim_run: 5, sow_doc: 6,
  };
  const activeArtifacts = (engagement.artifacts || [])
    .sort((a, b) => (ARTIFACT_ORDER[a.type] ?? 99) - (ARTIFACT_ORDER[b.type] ?? 99));
  const intakeDocs = (engagement.artifacts || []).filter(
    a => ['transcript', 'source_doc'].includes(a.type)
  );

 
  const hasApprovedArtifact = (type: string) =>
    (engagement.artifacts || []).some(a => a.type === type && a.status === 'approved');
  const stageBlockReason = (stageKey: string): string | null => {
    switch (stageKey) {
      case 'synthesis':
        return intakeDocs.some(d => d.status !== 'superseded')
          ? null
          : 'Upload at least one intake document first.';
      case 'ou_oa':
        return hasApprovedArtifact('synthesis')
          ? null
          : 'Needs an approved Synthesis. Approve it first, or use “Upload existing” above.';
      case 'plan':
        return hasApprovedArtifact('synthesis')
          ? null
          : 'Needs an approved Synthesis. Approve it first, or use “Upload existing” above.';
      case 'datasim':
        return hasApprovedArtifact('project_plan')
          ? null
          : 'Needs an approved Project Plan. Generate it first, or use “Upload existing” above.';
      case 'sow':
        return hasApprovedArtifact('project_plan')
          ? null
          : 'Needs an approved Project Plan. Generate it first, or use “Upload existing” above.';
      default:
        return null;
    }
  };

  const effectiveStage = viewStage ?? engagement.stage;
  const pmHandoffComplete = engagement.stage === 'won' && !!engagement.metadata?.pm_project_id;
  const canFinishRouter = pmHandoffComplete;

  return (
    <>
    {orchestratorCtx.inRouter && (
      <SmartRouterBanner
        stepLabel={`${(STAGES.find(s => s.key === effectiveStage)?.label) || effectiveStage} (Smart Router)`}
        canFinish={canFinishRouter}
        disabledReason="Push the engagement to Project Management on deal-win to finish the Smart Router plan."
        buildCompletionPayload={buildFinishPayload}
        buildResumeExtra={() => ({ subStep: effectiveStage, engagement_id: String(engagement.id) })}
      />
    )}
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <button
          onClick={() => navigate('/app/bd-engagements')}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 flex-shrink-0 mt-1"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          {editMode ? (
            <div className="space-y-3">
              <input
                className="w-full text-xl font-bold border border-blue-400 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-blue-500"
                value={editForm.name}
                onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
              />
              <div className="flex gap-3">
                <input
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  value={editForm.client_name}
                  onChange={e => setEditForm(f => ({ ...f, client_name: e.target.value }))}
                  placeholder="Client name"
                />
                <input
                  type="number"
                  className="w-40 border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  value={editForm.budget_cap}
                  onChange={e => setEditForm(f => ({ ...f, budget_cap: e.target.value }))}
                  placeholder="Budget cap"
                />
              </div>
              <textarea
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                value={editForm.notes}
                onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notes..."
              />
              <div className="flex gap-2">
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  className="flex items-center gap-1.5 px-4 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                >
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 truncate">{engagement.name}</h1>
                <button
                  onClick={() => setEditMode(true)}
                  className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                {engagement.client_name && (
                  <span className="font-medium text-gray-700">{engagement.client_name}</span>
                )}
                {engagement.budget_cap && (
                  <span>Budget: ${Number(engagement.budget_cap).toLocaleString()}</span>
                )}
                {engagement.notes && (
                  <span className="truncate max-w-xs">{engagement.notes}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* ── Left: Stage Stepper ── */}
        <div className="col-span-3">
          <div className="bg-white border border-gray-200 rounded-xl p-5 sticky top-6">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Pipeline Stages</h3>
            <div className="space-y-1">
              {STAGES.map((stage, idx) => {
                const status = getStageStatus(stage.key, engagement.stage, engagement.artifacts || []);
                const currentIdx = STAGES.findIndex(s => s.key === engagement.stage);
                const isNextStep = idx === currentIdx + 1;
                const isViewing = stage.key === (viewStage ?? engagement.stage);
                // Generatable stages are always reachable so a user can open any
                // step and upload an existing deliverable to skip ahead. Intake
                // and PM Handoff keep their sequential gating.
                const isUploadableStage = ['synthesis', 'ou_oa', 'plan', 'datasim', 'sow'].includes(stage.key);
                const isClickable = status === 'completed' || status === 'skipped' || status === 'current' ||
                  isUploadableStage ||
                  (isNextStep && stage.key !== 'won') ||
                  (stage.key === 'won' && engagement.stage === 'won');
                return (
                  <div key={stage.key} className="relative">
                    {idx < STAGES.length - 1 && (
                      <div className={`absolute left-3.5 top-8 w-0.5 h-6 ${status === 'completed' ? 'bg-green-400' : status === 'skipped' ? 'bg-orange-300' : 'bg-gray-200'}`} />
                    )}
                    <div
                      onClick={() => isClickable ? goToStage(stage.key) : undefined}
                      className={`flex items-start gap-3 p-2 rounded-lg ${
                        isViewing ? 'bg-blue-50' :
                        isClickable ? 'cursor-pointer hover:bg-gray-50' : ''
                      }`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {isViewing ? (
                          <div className="w-5 h-5 rounded-full border-2 border-blue-500 bg-blue-500 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-white" />
                          </div>
                        ) : status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : status === 'skipped' ? (
                          <div className="w-5 h-5 rounded-full border-2 border-orange-500 bg-orange-500 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-white" />
                          </div>
                        ) : (
                          <Circle className="w-5 h-5 text-gray-300" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${
                          isViewing ? 'text-blue-700' :
                          status === 'skipped' ? 'text-orange-700' :
                          status === 'completed' ? 'text-gray-700' : 'text-gray-400'
                        }`}>
                          {idx + 1}. {stage.label}
                        </p>
                        {(isViewing || status === 'skipped') && (
                          <p className={`text-xs mt-0.5 ${!isViewing && status === 'skipped' ? 'text-orange-500' : 'text-blue-500'}`}>
                            {stage.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-1">Current Stage</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg">
                <Target className="w-3.5 h-3.5" />
                {STAGES.find(s => s.key === engagement.stage)?.label || engagement.stage}
              </span>
            </div>
          </div>
        </div>

        {/* ── Center: Main Content ── */}
        <div className="col-span-6">
          {viewStage && viewStage !== engagement.stage && (
            <div className="mb-4 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <span className="text-sm text-amber-800">
                Viewing completed stage: <strong>{STAGES.find(s => s.key === viewStage)?.label}</strong>
              </span>
              <button
                onClick={() => setViewStage(null)}
                className="text-sm font-medium text-amber-700 hover:text-amber-900 underline"
              >
                ← Back to {STAGES.find(s => s.key === engagement.stage)?.label}
              </button>
            </div>
          )}
          {(() => {
            const activeStage = viewStage ?? engagement.stage;
            const STAGE_DELIVERABLE: Record<string, string> = {
              synthesis: 'synthesis',
              ou_oa: 'ou_oa_deck',
              plan: 'project_plan',
              datasim: 'datasim_run',
              sow: 'sow_doc',
            };
            const deliverableType = STAGE_DELIVERABLE[activeStage];
            if (!deliverableType) return null;
            return (
              <div className="mb-4 flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                <div className="min-w-0 pr-3">
                  <p className="text-sm font-medium text-slate-700">Already have this deliverable?</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Upload the finished file to use it as-is and skip ahead to this step.
                  </p>
                </div>
                <button
                  onClick={() => deliverableInputRef.current?.click()}
                  disabled={uploadingDeliverable}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:border-blue-400 disabled:opacity-50 flex-shrink-0"
                >
                  <Upload className="w-4 h-4" />
                  {uploadingDeliverable ? 'Uploading...' : 'Upload existing'}
                </button>
                <input
                  ref={deliverableInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.json"
                  className="hidden"
                  onChange={e => uploadDeliverable(deliverableType, e.target.files)}
                />
              </div>
            );
          })()}
          {(viewStage ?? engagement.stage) === 'intake' ? (
            /* ── Intake Stage UI ── */
            <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Document Intake</h3>
                <p className="text-sm text-gray-500">
                  Upload discovery documents — Otter transcripts, client emails, RFPs, internal notes.
                  These feed the AI Synthesis in the next stage.
                </p>
              </div>

              <EngagementBridgePanel engagementId={engagement.id} />

              {/* Type selector */}
              <div className="flex gap-2">
                {(['source_doc', 'transcript'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setArtifactType(t)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      artifactType === t
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {t === 'transcript' ? '🎤 Transcript' : '📁 Source Document'}
                  </button>
                ))}
              </div>

              {/* Drop zone */}
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  uploading
                    ? 'border-blue-300 bg-blue-50 cursor-not-allowed'
                    : dragOver
                      ? 'border-blue-400 bg-blue-50 cursor-pointer'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 cursor-pointer'
                }`}
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); if (!uploading) uploadFiles(e.dataTransfer.files); }}
              >
                {uploading ? (
                  <>
                    <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-sm font-medium text-blue-600">Uploading...</p>
                    <p className="text-xs text-blue-400 mt-1">Please wait</p>
                  </>
                ) : (
                  <>
                    <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? 'text-blue-500' : 'text-gray-300'}`} />
                    <p className="text-sm font-medium text-gray-600">Drop files here or click to browse</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, PowerPoint, TXT — up to 100MB each</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                  className="hidden"
                  onChange={e => uploadFiles(e.target.files)}
                />
              </div>

              {/* Uploaded docs list */}
              {intakeDocs.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Uploaded ({intakeDocs.length})
                  </p>
                  {intakeDocs.map(doc => (
                    <div key={doc.id} className="flex items-center gap-3 border border-gray-100 rounded-lg px-3 py-2.5">
                      <span className="text-base flex-shrink-0">
                        {doc.type === 'transcript' ? '🎤' : '📁'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {doc.metadata?.original_name || `File #${doc.id}`}
                        </p>
                        <p className="text-xs text-gray-400">
                          {doc.type === 'transcript' ? 'Transcript' : 'Source Doc'}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteArtifact(doc.id)}
                        disabled={deletingId === doc.id}
                        className="p-1.5 text-gray-300 hover:text-red-500 rounded flex-shrink-0 disabled:opacity-50"
                        title="Remove"
                      >
                        {deletingId === doc.id
                          ? <span className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin block" />
                          : <Trash2 className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Proceed button */}
              <div className="pt-2 border-t border-gray-100">
                <button
                  onClick={() => engagement.stage === 'intake' ? advanceStage() : setViewStage('synthesis')}
                  disabled={intakeDocs.length === 0 || advancing}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {advancing ? 'Proceeding...' : 'Proceed to Synthesis'}
                  <ArrowRight className="w-4 h-4" />
                </button>
                {intakeDocs.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1.5">Upload at least one document to proceed</p>
                )}
              </div>
            </div>
          ) : (viewStage ?? engagement.stage) === 'synthesis' ? (
            /* ── Synthesis Stage UI ── */
            (() => {
              const synthArtifact = (engagement.artifacts || []).find(
                a => a.type === 'synthesis'
              );
              const syn = synthArtifact?.content as any;

              return (
                <div className="space-y-4">
                  {/* Run / Re-run button */}
                  <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-gray-900">Discovery Synthesis</h3>
                        {synthArtifact?.status === 'approved' && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Approved
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        AI extracts pain points, stakeholders, opportunities and open questions from your documents.
                      </p>
                    </div>
                    <button
                      onClick={runSynthesis}
                      disabled={synthesizing}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex-shrink-0 ml-4"
                    >
                      <Brain className="w-4 h-4" />
                      {synthesizing ? 'Analyzing...' : synthArtifact ? 'Re-run Synthesis' : 'Run Synthesis'}
                    </button>
                  </div>

                  {synthesizing && (
                    <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4" />
                      <p className="text-sm font-medium text-gray-700">Analyzing documents...</p>
                      <p className="text-xs text-gray-400 mt-1">This takes 20–40 seconds</p>
                    </div>
                  )}

                  {!synthesizing && syn && (
                    <>
                      {/* Download Report */}
                      {synthArtifact?.file_ref && (
                        <div className="flex justify-end">
                          <button
                            onClick={async () => {
                              setDownloadingReport(true);
                              await downloadArtifact(synthArtifact.id);
                              setDownloadingReport(false);
                            }}
                            disabled={downloadingReport}
                            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                          >
                            {downloadingReport
                              ? <><div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin" /> Downloading...</>
                              : <><Download className="w-4 h-4" /> Download Report</>
                            }
                          </button>
                        </div>
                      )}

                      {/* Summary */}
                      {syn.summary && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Executive Summary</p>
                          <p className="text-sm text-blue-900">{syn.summary}</p>
                        </div>
                      )}

                      {/* Pain Points */}
                      {syn.pain_points?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Pain Points</p>
                          <div className="space-y-3">
                            {syn.pain_points.map((p: any, i: number) => (
                              <div key={i} className="flex items-start gap-3">
                                <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                                  p.severity === 'high' ? 'bg-red-500' :
                                  p.severity === 'medium' ? 'bg-yellow-400' : 'bg-gray-300'
                                }`} />
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-sm font-medium text-gray-800">{p.title}</p>
                                    {p.financial_impact && (
                                      <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-700 rounded font-medium">{p.financial_impact}</span>
                                    )}
                                  </div>
                                  {p.detail && <p className="text-xs text-gray-500 mt-0.5">{p.detail}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Stakeholders */}
                      {syn.stakeholders?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Stakeholders</p>
                          <div className="space-y-2">
                            {syn.stakeholders.map((s: any, i: number) => (
                              <div key={i} className="flex items-start gap-3 border border-gray-100 rounded-lg p-2.5">
                                <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-bold text-purple-700">
                                    {(s.name || s.title || '?')[0].toUpperCase()}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-gray-800">{s.name}</p>
                                    {s.influence && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                        s.influence === 'high' ? 'bg-red-100 text-red-700' :
                                        s.influence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-600'
                                      }`}>{s.influence}</span>
                                    )}
                                  </div>
                                  {s.title && <p className="text-xs text-gray-500">{s.title}</p>}
                                  {s.concerns && <p className="text-xs text-gray-600 mt-0.5 italic">"{s.concerns}"</p>}
                                  {s.notes && <p className="text-xs text-gray-400 mt-0.5">{s.notes}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Opportunities */}
                      {syn.opportunities?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Opportunities</p>
                          <div className="space-y-2">
                            {syn.opportunities.map((o: any, i: number) => (
                              <div key={i} className="flex items-start gap-3">
                                <span className="mt-0.5 text-green-500 text-base">✦</span>
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{o.title}</p>
                                  {o.detail && <p className="text-xs text-gray-500 mt-0.5">{o.detail}</p>}
                                  {o.value && <p className="text-xs text-green-600 mt-0.5 font-medium">{o.value}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Success Criteria */}
                      {syn.success_criteria?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Success Criteria</p>
                          <div className="space-y-2">
                            {syn.success_criteria.map((c: string, i: number) => (
                              <div key={i} className="flex items-start gap-2">
                                <span className="mt-0.5 text-blue-500 text-sm">◎</span>
                                <p className="text-sm text-gray-700">{c}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Constraints */}
                      {syn.constraints?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Constraints</p>
                          <div className="space-y-2">
                            {syn.constraints.map((c: any, i: number) => (
                              <div key={i} className="flex items-start gap-2">
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 mt-0.5 ${
                                  c.type === 'budget' ? 'bg-green-100 text-green-700' :
                                  c.type === 'timeline' ? 'bg-orange-100 text-orange-700' :
                                  c.type === 'regulatory' ? 'bg-red-100 text-red-700' :
                                  c.type === 'technical' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>{c.type}</span>
                                <p className="text-sm text-gray-700">{c.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Open Questions */}
                      {syn.open_questions?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Open Questions</p>
                          <div className="space-y-3">
                            {syn.open_questions.map((q: any, i: number) => (
                              <div key={i} className="flex items-start gap-2">
                                <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                                  q.priority === 'high' ? 'text-red-400' :
                                  q.priority === 'medium' ? 'text-yellow-400' : 'text-gray-300'
                                }`} />
                                <div>
                                  <p className="text-sm text-gray-700">{q.question}</p>
                                  {q.context && <p className="text-xs text-gray-400 mt-0.5">{q.context}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Already approved — show Proceed button */}
                      {synthArtifact?.status === 'approved' && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <p className="text-sm font-medium text-green-800">Synthesis approved</p>
                          </div>
                          <button
                            onClick={() => engagement.stage === 'synthesis' ? advanceStage() : setViewStage('ou_oa')}
                            disabled={advancing}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                          >
                            {advancing ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Moving...
                              </>
                            ) : (
                              <>Proceed to OU/OA Deck <ArrowRight className="w-4 h-4" /></>
                            )}
                          </button>
                        </div>
                      )}

                      {/* AI Council Review */}
                      {synthArtifact && synthArtifact.status !== 'approved' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-blue-900">AI Council — Approach Review</p>
                              <p className="text-xs text-blue-700 mt-0.5">Challenge the proposed approach before generating the OU/OA Deck</p>
                            </div>
                            {!councilReview && (
                              <button
                                onClick={runAiCouncil}
                                disabled={runningCouncil}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
                              >
                                {runningCouncil ? (
                                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Reviewing...</>
                                ) : (
                                  <>🧠 Run AI Council</>
                                )}
                              </button>
                            )}
                            {councilReview && (
                              <button onClick={() => setCouncilReview(null)} className="text-xs text-blue-500 hover:text-blue-700">Dismiss</button>
                            )}
                          </div>

                          {councilReview && (
                            <div className="space-y-3">
                              {/* Verdict */}
                              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                                councilReview.verdict === 'strong' ? 'bg-green-100 text-green-800' :
                                councilReview.verdict === 'promising' ? 'bg-blue-100 text-blue-800' :
                                'bg-amber-100 text-amber-800'
                              }`}>
                                <span>{councilReview.verdict === 'strong' ? '✅' : councilReview.verdict === 'promising' ? '👍' : '⚠️'}</span>
                                <span className="capitalize">{councilReview.verdict}</span>
                                <span className="font-normal">— {councilReview.summary}</span>
                              </div>

                              {/* Recommendations */}
                              {councilReview.recommendations?.length > 0 && (
                                <div className="space-y-2">
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Recommendations</p>
                                  {councilReview.recommendations.map((rec: any) => (
                                    <div key={rec.id} className="bg-white rounded-lg p-3 border border-blue-100">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                          rec.type === 'risk' ? 'bg-red-100 text-red-700' :
                                          rec.type === 'opportunity' ? 'bg-green-100 text-green-700' :
                                          rec.type === 'question' ? 'bg-purple-100 text-purple-700' :
                                          'bg-blue-100 text-blue-700'
                                        }`}>{rec.type}</span>
                                        <p className="text-sm font-semibold text-gray-900">{rec.title}</p>
                                      </div>
                                      <p className="text-xs text-gray-600">{rec.observation}</p>
                                      <p className="text-xs text-blue-700 mt-1">→ {rec.suggestion}</p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Challenged assumptions */}
                              {councilReview.challenged_assumptions?.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Challenged Assumptions</p>
                                  <div className="space-y-1">
                                    {councilReview.challenged_assumptions.map((a: any, i: number) => (
                                      <div key={i} className="bg-white rounded-lg px-3 py-2 border border-blue-100 text-xs">
                                        <span className="font-medium text-gray-800">{a.assumption}</span>
                                        <span className="text-gray-500"> — {a.challenge}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Key risks */}
                              {councilReview.key_risks?.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Key Risks</p>
                                  <div className="flex flex-wrap gap-2">
                                    {councilReview.key_risks.map((r: string, i: number) => (
                                      <span key={i} className="text-xs bg-red-50 text-red-700 border border-red-100 px-2 py-1 rounded-lg">{r}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Approve & Proceed */}
                      {synthArtifact && synthArtifact.status !== 'approved' && (
                        <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                          <p className="text-sm text-gray-600">Happy with the synthesis? Approve to unlock OU/OA Deck.</p>
                          <button
                            onClick={() => approveAndProceed(synthArtifact.id)}
                            disabled={approvingAndAdvancing}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                          >
                            {approvingAndAdvancing ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <><CheckCircle className="w-4 h-4" /> Approve & Proceed</>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {!synthesizing && !syn && (
                    <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
                      <Brain className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm text-gray-500">Click "Run Synthesis" to extract insights from your documents.</p>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (viewStage ?? engagement.stage) === 'ou_oa' ? (
            /* ── OU/OA Deck Stage UI ── */
            (() => {
              const deckArtifact = (engagement.artifacts || []).find(
                a => a.type === 'ou_oa_deck'
              );
              return (
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-gray-900">OU/OA Deck</h3>
                        {deckArtifact?.status === 'approved' && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Approved
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Generate a branded PowerPoint deck using synthesis insights and matched case studies.
                      </p>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0 ml-4">
                      <label
                        className="flex items-center gap-1.5 text-xs text-gray-500 mb-1.5 cursor-pointer"
                        title="Design the deck with Claude's PowerPoint skill instead of our standard template — slower and more expensive, but a richer, more original layout."
                      >
                        <input
                          type="checkbox"
                          checked={enhancedVisualization}
                          onChange={(e) => setEnhancedVisualization(e.target.checked)}
                          disabled={generatingDeck}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-400"
                        />
                        Enhanced visualization
                      </label>
                      <button
                        onClick={generateOuOa}
                        disabled={generatingDeck || !!stageBlockReason('ou_oa')}
                        title={stageBlockReason('ou_oa') || ''}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Brain className="w-4 h-4" />
                        {generatingDeck ? 'Generating...' : deckArtifact ? 'Regenerate Deck' : 'Generate Deck'}
                      </button>
                      {stageBlockReason('ou_oa') && (
                        <p className="text-xs text-amber-600 mt-1.5 text-right max-w-[16rem]">{stageBlockReason('ou_oa')}</p>
                      )}
                    </div>
                  </div>

                  {/* Our Approach — optional user notes. Fed into the OU/OA
                      generation prompt so consultant experience is woven
                      into slides 6-7 instead of being lost. */}
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label htmlFor="approach-notes" className="block text-sm font-semibold text-gray-900">
                          Our Approach — Your Notes
                          <span className="ml-2 text-xs font-normal text-gray-500">Optional</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Know how we'd tackle this, or have relevant experience? Add it here — the AI will weave it into the deck.
                        </p>
                      </div>
                      {savingApproachNotes && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin" />
                          Saving…
                        </span>
                      )}
                      {!savingApproachNotes && approachNotesDraft && approachNotesDraft === lastSavedApproachNotesRef.current && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Saved
                        </span>
                      )}
                    </div>
                    <textarea
                      id="approach-notes"
                      value={approachNotesDraft}
                      onChange={(e) => setApproachNotesDraft(e.target.value)}
                      onBlur={saveApproachNotes}
                      disabled={generatingDeck}
                      rows={4}
                      placeholder="e.g. We ran a similar engagement at AMEX last year — recommend Phase 0 discovery sprint before commitment. The client's data warehouse team prefers Snowflake-native solutions over external tooling."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-50 disabled:text-gray-500 resize-y"
                      maxLength={5000}
                    />
                    <div className="mt-1 text-xs text-gray-400 flex justify-between">
                      <span>Saved automatically when you click away from the box.</span>
                      <span>{approachNotesDraft.length}/5000</span>
                    </div>
                  </div>

                  {generatingDeck && (
                    <div className="space-y-3">
                      <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-4" />
                        <p className="text-sm font-medium text-gray-700">Generating OU/OA deck...</p>
                      </div>
                      <div className="flex items-center gap-4 bg-purple-600 text-white px-5 py-4 rounded-xl">
                        <div className="flex-shrink-0 w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <div>
                          <p className="font-semibold text-sm">Claude is building your PPTX</p>
                          <p className="text-xs text-purple-200 mt-0.5">This may take 10–12 minutes — please keep this tab open</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!generatingDeck && deckArtifact && (
                    <>
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <span className="text-2xl">📊</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {deckArtifact.metadata?.original_name || 'OU/OA Deck.pptx'}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {deckArtifact.metadata?.file_size
                                ? `${(deckArtifact.metadata.file_size / 1024).toFixed(0)} KB`
                                : ''} · Status: {deckArtifact.status}
                            </p>
                          </div>
                          <button
                            onClick={async () => { setDownloadingStageFile(true); await downloadArtifact(deckArtifact.id); setDownloadingStageFile(false); }}
                            disabled={downloadingStageFile}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                          >
                            {downloadingStageFile
                              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Downloading...</>
                              : <><Download className="w-4 h-4" /> Download PPTX</>
                            }
                          </button>
                        </div>
                      </div>

                      {/* Inline preview of the "Our Approach" slides extracted
                          from the persisted PPTX outline. Saves consultants from
                          downloading the full deck just to verify how their notes
                          shaped the output. */}
                      {(() => {
                        const slides = deckArtifact.content?.outline?.slides as Array<any> | undefined;
                        if (!Array.isArray(slides) || slides.length === 0) return null;
                        // Why TSG rides along: it is new, it is the slide the
                        // framework fills in, and a consultant should be able to
                        // read it without downloading the deck.
                        const approachSlides = slides.filter((s) =>
                          (typeof s?.title === 'string' && /approach/i.test(s.title)) ||
                          String(s?.type || '').toLowerCase() === 'why_tsg'
                        );
                        if (approachSlides.length === 0) return null;
                        const includesWhyTsg = approachSlides.some(
                          (s) => String(s?.type || '').toLowerCase() === 'why_tsg'
                        );
                        const notesIncluded = Boolean(deckArtifact.content?.approach_notes_used);
                        return (
                          <div className="bg-white border border-gray-200 rounded-xl p-5">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900">
                                  {includesWhyTsg ? 'Our Approach & Why TSG — Preview' : 'Our Approach — Preview'}
                                </h4>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Extracted from slides {approachSlides.length === 1 ? '' : `${approachSlides.length} `}of the generated deck.
                                </p>
                              </div>
                              {notesIncluded && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">
                                  <Brain className="w-3 h-3" /> Your notes incorporated
                                </span>
                              )}
                            </div>
                            {/* What the framework had to correct, or could not.
                                A deck with no delivery phase has to be visible
                                here, not buried in a server log. */}
                            {Array.isArray(deckArtifact.content?.framework_warnings) && deckArtifact.content.framework_warnings.length > 0 && (
                              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                                {deckArtifact.content.framework_warnings.map((w: string, i: number) => (
                                  <p key={i}>{w}</p>
                                ))}
                              </div>
                            )}
                            <div className="space-y-4">
                              {approachSlides.map((slide, idx) => (
                                <div key={idx} className="border-l-4 border-purple-400 pl-4 py-1">
                                  <h5 className="text-sm font-semibold text-gray-900 mb-1">{slide.title}</h5>
                                  {slide.subtitle && (
                                    <p className="text-xs text-gray-600 italic mb-2">{slide.subtitle}</p>
                                  )}
                                  {slide.headline && (
                                    <p className="text-sm font-medium text-purple-700 mb-2">{slide.headline}</p>
                                  )}
                                  {slide.body && (
                                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{slide.body}</p>
                                  )}
                                  {/* phase_columns shape: items have phase/weeks/title/bullets/deliverable */}
                                  {Array.isArray(slide.items) && slide.items.length > 0 && slide.items[0] && (slide.items[0].phase || slide.items[0].weeks) && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                                      {slide.items.map((ph: any, i: number) => (
                                        <div key={i} className="border border-gray-200 rounded-md p-2">
                                          <div className="flex items-baseline justify-between mb-1">
                                            <span className="text-xs font-semibold text-purple-700">{ph.phase || `Phase ${i + 1}`}</span>
                                            <span className="text-xs italic text-gray-500">{ph.weeks || ''}</span>
                                          </div>
                                          <p className="text-sm font-medium text-gray-900 mb-1">{ph.title || ''}</p>
                                          {Array.isArray(ph.bullets) && ph.bullets.length > 0 && (
                                            <ul className="text-xs text-gray-700 space-y-0.5">
                                              {ph.bullets.slice(0, 4).map((b: string, j: number) => (
                                                <li key={j} className="flex gap-1.5">
                                                  <span className="text-purple-500 flex-shrink-0">•</span>
                                                  <span>{b}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                          {ph.deliverable && (
                                            <p className="text-xs italic text-purple-700 mt-1.5">
                                              <span className="font-medium">Deliverable:</span> {ph.deliverable}
                                            </p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {/* why_tsg shape: items have headline/body */}
                                  {Array.isArray(slide.items) && slide.items.length > 0 && slide.items[0]?.headline && slide.items[0]?.body && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                                      {slide.items.map((d: any, i: number) => (
                                        <div key={i} className="border border-gray-200 rounded-md p-2">
                                          <p className="text-sm font-medium text-purple-700 mb-1">{d.headline}</p>
                                          <p className="text-xs text-gray-700 leading-relaxed">{d.body}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {/* Generic items list — strings or simple objects (for backwards compat) */}
                                  {Array.isArray(slide.items) && slide.items.length > 0 && !slide.items[0]?.phase && !slide.items[0]?.weeks && !(slide.items[0]?.headline && slide.items[0]?.body) && (
                                    <ul className="space-y-1 text-sm text-gray-700">
                                      {slide.items.map((it: any, i: number) => (
                                        <li key={i} className="flex gap-2">
                                          <span className="text-purple-500 flex-shrink-0">•</span>
                                          <span>
                                            {typeof it === 'string'
                                              ? it
                                              : it && typeof it === 'object'
                                                ? (it.title || it.label || it.headline || it.value || JSON.stringify(it))
                                                : String(it)}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {(slide.what || slide.soWhat || slide.nowWhat) && (
                                    <div className="space-y-1 text-sm text-gray-700">
                                      {slide.what && <p><span className="font-medium text-gray-600">What:</span> {slide.what}</p>}
                                      {slide.soWhat && <p><span className="font-medium text-gray-600">So what:</span> {slide.soWhat}</p>}
                                      {slide.nowWhat && <p><span className="font-medium text-gray-600">Now what:</span> {slide.nowWhat}</p>}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {deckArtifact.status !== 'approved' && (
                        <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                          <p className="text-sm text-gray-600 mr-4">Download and review the deck. Approve to unlock Project Plan.</p>
                          <button
                            onClick={() => approveDeckAndProceed(deckArtifact.id)}
                            disabled={approvingDeck}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex-shrink-0 disabled:opacity-50"
                          >
                            {approvingDeck ? (
                              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
                            ) : (
                              <><CheckCircle className="w-4 h-4" /> Approve & Proceed</>
                            )}
                          </button>
                        </div>
                      )}

                      {deckArtifact.status === 'approved' && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <p className="text-sm font-medium text-green-800">Deck approved</p>
                          </div>
                          <button
                            onClick={() => engagement.stage === 'ou_oa' ? advanceStage() : setViewStage('plan')}
                            disabled={advancing}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                          >
                            {advancing ? 'Moving...' : 'Proceed to Project Plan'}
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {!generatingDeck && !deckArtifact && (
                    <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
                      <span className="text-4xl mb-3 block">📊</span>
                      <p className="text-sm text-gray-500">Click "Generate Deck" to create your OU/OA PowerPoint.</p>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (viewStage ?? engagement.stage) === 'plan' ? (
            /* ── Project Plan Stage UI ── */
            (() => {
              const planArtifact = (engagement.artifacts || []).find(
                a => a.type === 'project_plan'
              );
              const plan = planArtifact?.content as any;

              return (
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-gray-900">Project Plan</h3>
                        {planArtifact?.status === 'approved' && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Approved
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        AI builds a backwards plan: outcomes → workstreams → tasks → pricing.
                      </p>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0 ml-4">
                      <button
                        onClick={generateProjectPlan}
                        disabled={generatingPlan || !!stageBlockReason('plan')}
                        title={stageBlockReason('plan') || ''}
                        className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg text-sm font-medium hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Brain className="w-4 h-4" />
                        {generatingPlan ? 'Generating...' : planArtifact ? 'Regenerate Plan' : 'Generate Plan'}
                      </button>
                      {stageBlockReason('plan') && (
                        <p className="text-xs text-amber-600 mt-1.5 text-right max-w-[16rem]">{stageBlockReason('plan')}</p>
                      )}
                    </div>
                  </div>

                  {!generatingPlan && (
                    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">Team capacity</h4>
                          <p className="text-xs text-gray-500 mt-0.5">Tell the planner how big the team is and how long the engagement runs. The plan will stay within these bounds.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="block">
                          <span className="text-xs font-medium text-gray-700">Team size (people)</span>
                          <input
                            type="number"
                            min={1}
                            max={20}
                            value={capacityTeamSize}
                            onChange={(e) => setCapacityTeamSize(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
                            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-yellow-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-gray-400 mt-0.5 block">Typical engagement: 3–6 people</span>
                        </label>
                        <label className="block">
                          <span className="text-xs font-medium text-gray-700">Engagement duration (weeks)</span>
                          <input
                            type="number"
                            min={1}
                            max={52}
                            value={capacityWeeks}
                            onChange={(e) => setCapacityWeeks(Math.max(1, Math.min(52, parseInt(e.target.value, 10) || 1)))}
                            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-yellow-500 focus:outline-none"
                          />
                          <span className="text-[10px] text-gray-400 mt-0.5 block">Typical engagement: 4–12 weeks (1–3 months)</span>
                        </label>
                      </div>

                      {canEditRoleAllocation && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-700">Per-role allocation (optional)</span>
                          <button
                            type="button"
                            onClick={() => setCapacityComposition([...capacityComposition, { role: '', allocation_pct: 100 }])}
                            disabled={rateCardRoleNames.length === 0 || capacityComposition.length >= rateCardRoleNames.length}
                            className="text-xs text-yellow-700 hover:text-yellow-800 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                            title={rateCardRoleNames.length === 0 ? 'Add roles to the Company Brain rate card first' : capacityComposition.length >= rateCardRoleNames.length ? 'All rate card roles added' : ''}
                          >
                            + Add role
                          </button>
                        </div>
                        {rateCardRoleNames.length === 0 ? (
                          <p className="text-[11px] text-amber-700 italic">
                            No rate card configured. Add roles in <strong>Company Brain → Rate Card</strong> first, then refresh — the dropdown here will populate from those roles so the planner can match them.
                          </p>
                        ) : capacityComposition.length === 0 ? (
                          <p className="text-[11px] text-gray-400 italic">No per-role allocation set — the planner will assume each team member is full-time. Add roles to mark specialists as part-time.</p>
                        ) : (
                          <div className="space-y-2">
                            {capacityComposition.map((c, i) => {
                              const usedElsewhere = new Set(
                                capacityComposition.filter((_, idx) => idx !== i).map(x => (x.role || '').toLowerCase())
                              );
                              return (
                                <div key={i} className="flex items-center gap-2">
                                  <select
                                    value={c.role}
                                    onChange={(e) => {
                                      const next = capacityComposition.slice();
                                      next[i] = { ...next[i], role: e.target.value };
                                      setCapacityComposition(next);
                                    }}
                                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:border-yellow-500 focus:outline-none bg-white"
                                  >
                                    <option value="">— Pick a role —</option>
                                    {rateCardRoleNames.map(name => (
                                      <option
                                        key={name}
                                        value={name}
                                        disabled={usedElsewhere.has(name.toLowerCase()) && name !== c.role}
                                      >
                                        {name}{usedElsewhere.has(name.toLowerCase()) && name !== c.role ? ' (already added)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    value={c.allocation_pct}
                                    onChange={(e) => {
                                      const next = capacityComposition.slice();
                                      next[i] = { ...next[i], allocation_pct: parseInt(e.target.value, 10) };
                                      setCapacityComposition(next);
                                    }}
                                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:border-yellow-500 focus:outline-none bg-white"
                                  >
                                    <option value={100}>Full-time (100%)</option>
                                    <option value={75}>3 days/week (75%)</option>
                                    <option value={50}>Half-time (50%)</option>
                                    <option value={40}>2 days/week (40%)</option>
                                    <option value={20}>1 day/week (20%)</option>
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => setCapacityComposition(capacityComposition.filter((_, idx) => idx !== i))}
                                    className="text-gray-400 hover:text-red-600 px-2"
                                    title="Remove role"
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                  )}

                  {generatingPlan && (
                    <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-600 mx-auto mb-4" />
                      <p className="text-sm font-medium text-gray-700">Building project plan...</p>
                      <p className="text-xs text-gray-400 mt-1">AI is working backwards from outcomes — takes 30–60 seconds</p>
                    </div>
                  )}

                  {!generatingPlan && plan && (
                    <>
                      {/* Objective */}
                      {plan.objective && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                          <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-1">Objective</p>
                          <p className="text-sm text-yellow-900">{plan.objective}</p>
                        </div>
                      )}

                      {/* Summary cards */}
                      {plan.summary && (
                        <>
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              { label: 'Timeline', value: `${plan.timeline_weeks} weeks` },
                              { label: 'Total Hours', value: plan.summary.total_hours?.toLocaleString() },
                              // "Our Cost" rather than "Est. Cost": this is now
                              // the engine's Subtotal Payable — what the work
                              // costs us. It used to be hours x the client's
                              // bill rate, which is revenue, not cost.
                              { label: 'Our Cost', value: plan.summary.total_cost > 0 ? `$${plan.summary.total_cost.toLocaleString()}` : '—' },
                              { label: 'Total SOW', value: plan.summary.recommended_price ? `$${plan.summary.recommended_price.toLocaleString()}` : '—' },
                            ].map(({ label, value }) => (
                              <div key={label} className="bg-white border border-gray-200 rounded-xl p-3 text-center">
                                <p className="text-xs text-gray-500">{label}</p>
                                <p className="text-lg font-bold text-gray-900 mt-0.5">{value}</p>
                              </div>
                            ))}
                          </div>
                          <EstimateDisclaimer className="mt-1" />
                          {plan.team_capacity && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 text-xs text-yellow-900 flex items-center justify-between">
                              <span>
                                Built for <strong>{plan.team_capacity.team_size}</strong> people over <strong>{plan.team_capacity.engagement_weeks}</strong> weeks
                                {Array.isArray(plan.team_capacity.composition) && plan.team_capacity.composition.length > 0
                                  ? ` · ${plan.team_capacity.composition.length} role(s) with custom allocation`
                                  : ''}
                              </span>
                              {typeof plan.capacity_utilization === 'number' && (
                                <span className="font-medium">
                                  {plan.capacity_utilization}% capacity used
                                </span>
                              )}
                            </div>
                          )}
                          {/* Why the plan has no price, when it has none. An
                              empty Est. Cost with no explanation is unactionable. */}
                          {Array.isArray(planArtifact?.metadata?.rate_card_warnings) && planArtifact.metadata.rate_card_warnings.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-900">
                              <p className="font-semibold mb-1">Pricing could not be applied</p>
                              {planArtifact.metadata.rate_card_warnings.map((w: string, i: number) => (
                                <p key={i}>{w}</p>
                              ))}
                            </div>
                          )}

                          {/* A plan generated before the pricing engine was
                              wired keeps the old arithmetic in plan.summary —
                              cost = hours x bill rate, price = cost / (1 -
                              margin floor) — and the cards above render it with
                              nothing to say it is stale. On a test engagement
                              that number was $560,250 against the engine's
                              $287,373, so it is not a small drift: it is roughly
                              twice the real price, and someone could quote it. */}
                          {plan.summary && !plan.pricing && plan.summary.recommended_price > 0 && (
                            <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-xs text-amber-900">
                              <p className="font-semibold">These figures came from the old pricing model</p>
                              <p className="mt-0.5">
                                This plan was priced before the rate card engine was connected, so the cost above is
                                the client's bill rate rather than our cost, and the price is that figure divided by
                                the margin floor. It runs high. Re-price the plan to get the real numbers.
                              </p>
                              <button
                                onClick={() => repricePlan(null, {}, [])}
                                disabled={savingPricing}
                                className="mt-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50"
                              >
                                {savingPricing ? 'Pricing…' : 'Price this plan'}
                              </button>
                            </div>
                          )}

                          {/* ── Pricing model ──────────────────────────────────
                              Ryan's workbook arithmetic, from the pricing
                              engine. The point of showing each component rather
                              than just a price is his own: the margin is hidden
                              in three places — a baseline fee folded into the
                              client's hours with no line item, a markup written
                              inside the formula, and T&E. A single
                              "Recommended Price" hides exactly that. */}
                          {(() => {
                            const pricing = plan.pricing as any;
                            if (!pricing) return null;

                            if (!pricing.available) {
                              return (
                                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-xs text-rose-900">
                                  <p className="text-xs font-semibold uppercase tracking-wide mb-1">No pricing model</p>
                                  <p>{pricing.detail}</p>
                                  <p className="mt-2 text-rose-700">
                                    The plan's hours stand. Nothing has been priced, rather than pricing
                                    this client off another client's rates.
                                  </p>
                                </div>
                              );
                            }

                            const t = pricing.totals;
                            const m = pricing.margins;
                            const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
                            const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
                            const marginFloor = plan.summary?.margin_floor ?? 0.6;
                            const form = pricingForm ?? {
                              baselineCostPerWeek: String(pricing.settings.baselineCostPerWeek ?? 4000),
                              additionalMarginPct: String(Number(((pricing.settings.additionalMarginPct ?? 0) * 100).toFixed(4))),
                              tePct: String(Number(((pricing.settings.tePct ?? 0) * 100).toFixed(4))),
                              teOnOwnLine: pricing.settings.teOnOwnLine !== false,
                            };
                            const billableRows = (pricing.rows || []).filter((r: any) => r.billable);

                            return (
                              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Pricing model</p>
                                    <p className="text-[11px] text-gray-500 mt-0.5">
                                      Rate card account <strong>{pricing.account || '—'}</strong>
                                      {' · '}baseline charged over {pricing.baseline_weeks} week(s)
                                    </p>
                                  </div>
                                  <button
                                    onClick={downloadPricingModel}
                                    disabled={downloadingPricingModel}
                                    className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                  >
                                    {downloadingPricingModel ? 'Preparing…' : 'Download Excel model'}
                                  </button>
                                </div>

                                {/* The quote is short by whole roles. Loud and
                                    above the numbers, because the number below
                                    looks finished — and the SOW route refuses
                                    on this same flag. */}
                                {pricing.price_incomplete && (
                                  <div className="bg-rose-50 border border-rose-300 rounded-lg px-3 py-2.5 text-xs text-rose-900">
                                    <p className="font-semibold">Do not quote this price yet</p>
                                    <p className="mt-0.5">
                                      It covers {(pricing.hours.planned - pricing.hours.unbilled).toLocaleString()} of{' '}
                                      {pricing.hours.planned.toLocaleString()} planned hours.{' '}
                                      {(pricing.unbilled_roles || []).map((r: any) => `${r.role} (${r.plan_hours} h)`).join(', ')}
                                      {' '}{(pricing.unbilled_roles || []).length === 1 ? 'has' : 'have'} no bill rate on account "{pricing.account}",
                                      so {(pricing.unbilled_roles || []).length === 1 ? 'it is' : 'they are'} missing from the total.
                                    </p>
                                    <p className="mt-1 text-rose-700">SOW generation is blocked until the rate card has those rows.</p>
                                  </div>
                                )}

                                {/* Where the money goes, in the order the engine applies it. */}
                                <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 text-xs">
                                  {[
                                    ['Our cost (Subtotal Payable)', money(t.totalSubtotalPayable), null],
                                    ['Baseline cost', money(t.baselineCost), 'folded into the client’s hours — no line item'],
                                    ['Total billable line items', money(t.totalBillableLineItems), 'after the 3% / 80% risk markup'],
                                    ['Initial gross margin', pct(m.initialGrossMargin), null],
                                    ['Additional margin', money(t.additionalMarginDollar), null],
                                    [`T&E${form.teOnOwnLine ? '' : ' (folded into hours)'}`, money(t.teAmount), null],
                                  ].map(([label, value, note]) => (
                                    <div key={label as string} className="flex items-baseline justify-between px-3 py-2">
                                      <span className="text-gray-600">
                                        {label}
                                        {note && <span className="text-gray-400"> · {note}</span>}
                                      </span>
                                      <span className="font-medium text-gray-900 tabular-nums">{value}</span>
                                    </div>
                                  ))}
                                  <div className="flex items-baseline justify-between px-3 py-2 bg-gray-50">
                                    <span className="font-semibold text-gray-900">Total SOW</span>
                                    <span className="font-bold text-gray-900 tabular-nums">{money(t.totalSOW)}</span>
                                  </div>
                                  <div className="flex items-baseline justify-between px-3 py-2">
                                    <span className="text-gray-600">
                                      Actual gross margin
                                      {/* A role costing $0 makes this read high.
                                          The client's price is unaffected, so it
                                          is marked rather than withheld. */}
                                      {pricing.margin_reliable === false && (
                                        <span className="text-amber-700">
                                          {' '}· reads high, {pricing.unpriced_roles.join(', ')}{' '}
                                          {pricing.unpriced_roles.length === 1 ? 'has' : 'have'} no cost on the rate card
                                        </span>
                                      )}
                                    </span>
                                    <span className={`font-bold tabular-nums ${pricing.margin_reliable === false ? 'text-amber-700' : m.actualGrossMargin >= marginFloor ? 'text-emerald-700' : 'text-amber-700'}`}>
                                      {pct(m.actualGrossMargin)}
                                      <span className="font-normal text-gray-400"> vs {pct(marginFloor)} floor</span>
                                    </span>
                                  </div>
                                </div>

                                {/* Planned hours vs what the client is quoted.
                                    Rule 7 pads everyone's hours with the
                                    baseline, the additional margin and (when it
                                    is not on its own line) T&E — so these two
                                    numbers are never the same, and the gap is
                                    the thing a consultant needs to be able to
                                    defend in the room. */}
                                {/* The gap is normally positive — rule 7 pads
                                    everyone's hours. It goes NEGATIVE when a
                                    role has no bill rate, because an unbilled
                                    role contributes 0 SOW hours. Saying
                                    "+-141 hrs carry the baseline" there is both
                                    wrong and reassuring about a broken quote. */}
                                {(() => {
                                  const delta = pricing.hours.sow - pricing.hours.planned;
                                  const short = delta < 0;
                                  return (
                                    <div className={`rounded-lg border px-3 py-2 text-xs flex items-center justify-between ${short ? 'bg-rose-50 border-rose-200 text-rose-900' : 'bg-blue-50 border-blue-200 text-blue-900'}`}>
                                      <span>Planned <strong>{pricing.hours.planned.toLocaleString()} hrs</strong> · client sees <strong>{pricing.hours.sow.toLocaleString()} hrs</strong></span>
                                      <span className={short ? 'text-rose-700' : 'text-blue-700'}>
                                        {short
                                          ? `${Math.abs(delta).toLocaleString()} hrs short — the unbilled roles bill nothing`
                                          : `+${delta.toLocaleString()} hrs carry the baseline and margin`}
                                      </span>
                                    </div>
                                  );
                                })()}

                                {/* What the client pays if they take only the
                                    need-to-have work. The saving is smaller
                                    than the hours suggest because the baseline
                                    cost is charged over the engagement's weeks
                                    either way — which is itself the useful part
                                    of the answer. */}
                                {pricing.need_to_have_only && (
                                  <div className="rounded-lg border border-gray-200 p-3 text-xs">
                                    <p className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-2">If the client drops the optional work</p>
                                    <div className="grid grid-cols-3 gap-3">
                                      <div>
                                        <p className="text-gray-500">Need-to-have only</p>
                                        <p className="text-base font-bold text-gray-900 mt-0.5">{money(pricing.need_to_have_only.total_sow)}</p>
                                        <p className="text-[10px] text-gray-400">{pricing.need_to_have_only.planned_hours.toLocaleString()} hrs planned</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">Full scope</p>
                                        <p className="text-base font-bold text-gray-900 mt-0.5">{money(t.totalSOW)}</p>
                                        <p className="text-[10px] text-gray-400">{pricing.hours.planned.toLocaleString()} hrs planned</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">Difference</p>
                                        <p className="text-base font-bold text-amber-700 mt-0.5">
                                          {money(t.totalSOW - pricing.need_to_have_only.total_sow)}
                                        </p>
                                        <p className="text-[10px] text-gray-400">
                                          {t.totalSOW > 0
                                            ? `${Math.round(((t.totalSOW - pricing.need_to_have_only.total_sow) / t.totalSOW) * 100)}% of the fee`
                                            : '—'}
                                        </p>
                                      </div>
                                    </div>
                                    <p className="text-[11px] text-gray-500 mt-2">
                                      Margin on the reduced scope: {pct(pricing.need_to_have_only.actual_gross_margin)}.
                                      The {money(pricing.need_to_have_only.baseline_cost)} baseline is charged over the
                                      engagement's weeks either way, so it does not come out with the optional work.
                                    </p>
                                  </div>
                                )}

                                {/* Per-role staffing, derived from the plan's own
                                    tasks, with the Flat/Cost override. */}
                                {billableRows.length > 0 && (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-left text-gray-500 border-b border-gray-200">
                                          <th className="py-1.5 pr-2 font-medium">Role</th>
                                          <th className="py-1.5 px-2 font-medium text-right">Plan hrs</th>
                                          <th className="py-1.5 px-2 font-medium text-right">Bill rate</th>
                                          <th className="py-1.5 px-2 font-medium text-right">SOW hrs</th>
                                          <th className="py-1.5 px-2 font-medium text-right">SOW subtotal</th>
                                          <th className="py-1.5 pl-2 font-medium text-right">Cost override</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {billableRows.map((r: any) => (
                                          <tr key={r.role}>
                                            <td className="py-1.5 pr-2 text-gray-900">
                                              {r.role}
                                              {/* Two different problems with two
                                                  different fixes: add a row, or
                                                  fill in the cost on the row
                                                  that is already there. */}
                                              {!r.has_rate_card_row ? (
                                                <span className="ml-1 text-[10px] text-amber-700">no rate card row</span>
                                              ) : r.has_rate_card_cost === false && r.override == null ? (
                                                <span className="ml-1 text-[10px] text-amber-700">no cost on rate card</span>
                                              ) : null}
                                              {r.priority === 'nice_to_have' && (
                                                <span className="ml-1 text-[10px] text-amber-700">optional</span>
                                              )}
                                              {r.priority === 'mixed' && r.nice_hours > 0 && (
                                                <span className="ml-1 text-[10px] text-gray-400">{r.nice_hours}h optional</span>
                                              )}
                                            </td>
                                            <td className="py-1.5 px-2 text-right tabular-nums text-gray-700">{r.plan_hours?.toLocaleString()}</td>
                                            <td className="py-1.5 px-2 text-right tabular-nums text-gray-700">{r.billRate ? `$${r.billRate}` : '—'}</td>
                                            <td className="py-1.5 px-2 text-right tabular-nums text-gray-700">{r.sowHours?.toLocaleString()}</td>
                                            <td className="py-1.5 px-2 text-right tabular-nums text-gray-900">{money(r.sowSubtotal || 0)}</td>
                                            <td className="py-1.5 pl-2 text-right">
                                              <input
                                                type="number"
                                                min={0}
                                                placeholder={r.unitCost ? String(r.unitCost) : '—'}
                                                value={pricingOverrides[r.role] ?? (r.override != null ? String(r.override) : '')}
                                                onChange={(e) => setPricingOverrides(prev => ({ ...prev, [r.role]: e.target.value }))}
                                                className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-xs"
                                              />
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    <p className="text-[11px] text-gray-500 mt-1">
                                      Cost override replaces our hourly cost for that role. The placeholder is the rate card's own cost.
                                    </p>
                                  </div>
                                )}

                                {/* The four engagement-level settings. Ryan
                                    (2026-10-07) on the baseline: "It's per
                                    engagement" — which is why these are here and
                                    not only on the rate card. */}
                                <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-3">
                                  <p className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide">Settings for this engagement</p>
                                  <div className="grid grid-cols-3 gap-3">
                                    {[
                                      { key: 'baselineCostPerWeek' as const, label: 'Baseline $/week', step: '100' },
                                      { key: 'additionalMarginPct' as const, label: 'Additional margin %', step: '0.5' },
                                      { key: 'tePct' as const, label: 'T&E %', step: '0.5' },
                                    ].map(({ key, label, step }) => (
                                      <div key={key}>
                                        <label className="block text-[11px] text-gray-600 mb-1">{label}</label>
                                        <input
                                          type="number"
                                          min={0}
                                          step={step}
                                          value={form[key]}
                                          onChange={(e) => setPricingForm({ ...form, [key]: e.target.value })}
                                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
                                        />
                                        <p className="text-[10px] text-gray-400 mt-0.5">
                                          from {pricing.setting_sources?.[key] === 'engagement' ? 'this engagement' : pricing.setting_sources?.[key] === 'rate_card' ? 'the rate card' : 'the default'}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                  <label className="flex items-center gap-2 text-xs text-gray-700">
                                    <input
                                      type="checkbox"
                                      checked={form.teOnOwnLine}
                                      onChange={(e) => setPricingForm({ ...form, teOnOwnLine: e.target.checked })}
                                      className="rounded border-gray-300"
                                    />
                                    T&amp;E on its own line
                                    <span className="text-gray-400">
                                      (unchecked folds it into everyone's hours instead)
                                    </span>
                                  </label>
                                  <button
                                    onClick={() => repricePlan(form, pricingOverrides, billableRows.map((r: any) => r.role))}
                                    disabled={savingPricing}
                                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                                  >
                                    {savingPricing ? 'Re-pricing…' : 'Save & re-price'}
                                  </button>
                                </div>

                                {Array.isArray(pricing.warnings) && pricing.warnings.length > 0 && (
                                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 space-y-1">
                                    <p className="font-semibold">Review before this price goes out</p>
                                    {pricing.warnings.map((w: string, i: number) => (
                                      <p key={i}>• {w}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          {plan.priority_summary && (plan.priority_summary.need_to_have?.tasks > 0 || plan.priority_summary.nice_to_have?.tasks > 0) && (
                            <div className="bg-white border border-gray-200 rounded-xl p-4">
                              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">What this client actually needs</p>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                  <p className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wide">Need to have</p>
                                  <p className="text-lg font-bold text-emerald-900 mt-0.5">
                                    {plan.priority_summary.need_to_have.hours.toLocaleString()} hrs
                                    {plan.priority_summary.need_hours_pct != null && (
                                      <span className="text-xs font-medium text-emerald-700"> · {plan.priority_summary.need_hours_pct}%</span>
                                    )}
                                  </p>
                                  <p className="text-[11px] text-emerald-700 mt-0.5">
                                    {plan.priority_summary.need_to_have.workstreams} workstream(s)
                                    {plan.priority_summary.need_to_have.cost > 0 && ` · $${plan.priority_summary.need_to_have.cost.toLocaleString()}`}
                                  </p>
                                </div>
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                  <p className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide">Nice to have</p>
                                  <p className="text-lg font-bold text-amber-900 mt-0.5">
                                    {plan.priority_summary.nice_to_have.hours.toLocaleString()} hrs
                                    {plan.priority_summary.need_hours_pct != null && (
                                      <span className="text-xs font-medium text-amber-700"> · {100 - plan.priority_summary.need_hours_pct}%</span>
                                    )}
                                  </p>
                                  <p className="text-[11px] text-amber-700 mt-0.5">
                                    {plan.priority_summary.nice_to_have.workstreams} workstream(s)
                                    {plan.priority_summary.nice_to_have.cost > 0 && ` · $${plan.priority_summary.nice_to_have.cost.toLocaleString()}`}
                                  </p>
                                </div>
                              </div>
                              {Array.isArray(plan.priority_summary.unlinked_workstreams) && plan.priority_summary.unlinked_workstreams.length > 0 && (
                                <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mt-3">
                                  <strong>Check these:</strong> {plan.priority_summary.unlinked_workstreams.join(', ')} —
                                  {' '}not tied to any pain point from discovery. Either they are real and discovery missed something, or the plan invented work.
                                </p>
                              )}
                            </div>
                          )}
                          {plan.unknowns_summary && plan.unknowns_summary.unknown_tasks > 0 && (() => {
                            const research = (engagement?.artifacts || []).find((a: any) => a.type === 'unknowns_research');
                            const findings = research?.content?.findings || [];
                            return (
                              <div className="bg-white border border-gray-200 rounded-xl p-4">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                  <div>
                                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Estimates nobody is sure about</p>
                                    <p className="text-[11px] text-gray-500 mt-0.5">
                                      {plan.unknowns_summary.unknown_tasks} of {plan.unknowns_summary.total_tasks} tasks
                                      {' '}({plan.unknowns_summary.unknown_pct}%) · {plan.unknowns_summary.unknown_hours.toLocaleString()} hours resting on a guess
                                    </p>
                                  </div>
                                  <button
                                    onClick={researchUnknowns}
                                    disabled={researchingUnknowns}
                                    className="text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    {researchingUnknowns ? 'Researching…' : findings.length > 0 ? 'Research again' : 'Research these'}
                                  </button>
                                </div>

                                {findings.length > 0 && (
                                  <ul className="space-y-2 mt-3">
                                    {findings.map((f: any) => (
                                      <li key={f.id} className="border border-gray-200 rounded-lg p-3">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-xs font-semibold text-gray-800">{f.task}</span>
                                          <span className="text-[10px] text-gray-500">{f.workstream}</span>
                                          <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                                            f.evidence === 'cited'
                                              ? 'bg-blue-50 text-blue-800 border-blue-200'
                                              : 'bg-gray-100 text-gray-600 border-gray-300'
                                          }`}>
                                            {f.evidence === 'cited' ? 'Cited' : 'No sources'}
                                          </span>
                                        </div>
                                        <p className="text-[11px] text-gray-600 mt-1">
                                          Plan says <strong>{f.hours ?? '—'} hrs</strong>
                                          {f.found
                                            ? <> · research suggests <strong>{f.low_hours ?? '?'}–{f.high_hours ?? '?'} hrs</strong></>
                                            : <> · <span className="text-gray-500">no credible benchmark found</span></>}
                                        </p>
                                        {f.basis && <p className="text-[11px] text-gray-500 mt-0.5">{f.basis}</p>}
                                        {f.caveat && <p className="text-[11px] text-amber-700 mt-0.5">{f.caveat}</p>}
                                        {Array.isArray(f.sources) && f.sources.length > 0 && (
                                          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                                            {/* Numbered because several sources often share a domain —
                                                different pages on the same site read as duplicates without it. */}
                                            {f.sources.map((s: any, si: number) => (
                                              <a key={si} href={s.url} target="_blank" rel="noopener noreferrer"
                                                 title={s.url}
                                                 className="text-[10px] text-blue-700 underline hover:text-blue-900">
                                                {si + 1}. {s.title}
                                              </a>
                                            ))}
                                          </div>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                <p className="text-[11px] text-gray-500 mt-2">
                                  Nothing here is applied to the plan automatically — check the sources, then edit the hours yourself.
                                </p>
                              </div>
                            );
                          })()}
                          {Array.isArray(plan.descope_recommendations) && plan.descope_recommendations.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                              <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">Descoped from this plan ({plan.descope_recommendations.length})</p>
                              <p className="text-[11px] text-amber-700 mb-2">The planner cut these items because they exceed the team capacity above. Increase team size or duration if you want them back in.</p>
                              <ul className="space-y-1.5">
                                {plan.descope_recommendations.map((d: any, i: number) => (
                                  <li key={i} className="text-xs text-amber-900">
                                    <strong>{d.item || 'Item'}</strong>{d.reason ? <> — <span className="text-amber-700">{d.reason}</span></> : null}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}

                      {/* Workstreams */}
                      {(plan.workstreams || []).map((ws: any, wi: number) => (
                        <div key={wi} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold text-gray-800">{ws.name}</p>
                              {ws.priority && (
                                <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${
                                  ws.priority === 'nice_to_have'
                                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                                    : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                }`}>
                                  {ws.priority === 'nice_to_have' ? 'Nice to have' : 'Need to have'}
                                </span>
                              )}
                            </div>
                            {/* Which client problem this workstream exists to solve. */}
                            {Array.isArray(ws.addresses_pain_points) && ws.addresses_pain_points.length > 0 && (
                              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                                {ws.addresses_pain_points.map((pid: string) => {
                                  const pp = (plan.pain_point_index || []).find((p: any) => p.id === pid);
                                  return (
                                    <span key={pid} title={pp?.detail || ''} className="text-[10px] text-blue-800 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                                      {pid}{pp?.title ? ` · ${pp.title}` : ''}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {ws.deliverables?.length > 0 && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                Deliverables: {ws.deliverables.join(' · ')}
                              </p>
                            )}
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                                  <th className="text-left px-3 py-2">Task</th>
                                  <th className="text-left px-3 py-2">Role</th>
                                  <th className="text-right px-3 py-2">Hrs</th>
                                  <th className="text-right px-3 py-2">Cost</th>
                                  <th className="text-right px-3 py-2">Weeks</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(ws.tasks || []).map((task: any, ti: number) => (
                                  <tr key={ti} className="border-t border-gray-100">
                                    <td className="px-3 py-2 text-gray-800">
                                      {task.name}
                                      {task.estimate_confidence === 'unknown' && (
                                        <span
                                          title={task.unknown_reason || 'The planner was not confident in this estimate'}
                                          className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-300"
                                        >
                                          Unsure
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600">{task.role}</td>
                                    <td className="px-3 py-2 text-right text-gray-700">{task.hours}</td>
                                    <td className="px-3 py-2 text-right text-gray-700">
                                      {task.cost > 0 ? `$${task.cost.toLocaleString()}` : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-500">
                                      W{task.week_start}–W{task.week_end}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}

                      {/* Download + Approve */}
                      <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-3">
                        <button
                          onClick={async () => {
                            setDownloadingStageFile(true);
                            await downloadArtifact(planArtifact!.id);
                            setDownloadingStageFile(false);
                          }}
                          disabled={downloadingStageFile}
                          className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {downloadingStageFile
                            ? <><div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> Downloading...</>
                            : <><Download className="w-4 h-4" /> Download Excel</>
                          }
                        </button>
                        {planArtifact?.status === 'approved' ? (
                          <button
                            onClick={() => engagement.stage === 'plan' ? advanceStage() : setViewStage('datasim')}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                          >
                            <ArrowRight className="w-4 h-4" /> Proceed to DataSim
                          </button>
                        ) : (
                          <button
                            onClick={() => approvePlanAndProceed(planArtifact!.id)}
                            disabled={approvingPlan}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                          >
                            {approvingPlan ? (
                              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
                            ) : (
                              <><CheckCircle className="w-4 h-4" /> Approve & Proceed</>
                            )}
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  {!generatingPlan && !plan && (
                    <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
                      <span className="text-4xl mb-3 block">📈</span>
                      <p className="text-sm text-gray-500">Click "Generate Plan" to build your project plan with pricing.</p>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (viewStage ?? engagement.stage) === 'datasim' ? (
            /* ── DataSim Stage UI ── */
            (() => {
              const simArtifact = (engagement.artifacts || []).find(
                a => a.type === 'datasim_run'
              );
              const sim = simArtifact?.content as any;

              const CheckBadge = ({ pass }: { pass: boolean | null }) => (
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  pass === null ? 'bg-gray-100 text-gray-500' :
                  pass ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {pass === null ? 'N/A' : pass ? '✓ PASS' : '✗ FAIL'}
                </span>
              );

              return (
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-gray-900">DataSim Pressure Test</h3>
                        {simArtifact?.status === 'approved' && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Approved
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        10,000 Monte Carlo iterations — checks margin floor ({((engagement.margin_floor_override || 0.6) * 100).toFixed(0)}%) and workload ceiling (45 hr/wk).
                      </p>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0 ml-4">
                      <button
                        onClick={runDataSim}
                        disabled={runningDataSim || !!stageBlockReason('datasim')}
                        title={stageBlockReason('datasim') || ''}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        🎲 {runningDataSim ? 'Running...' : simArtifact ? 'Re-run Sim' : 'Run DataSim'}
                      </button>
                      {stageBlockReason('datasim') && (
                        <p className="text-xs text-amber-600 mt-1.5 text-right max-w-[16rem]">{stageBlockReason('datasim')}</p>
                      )}
                    </div>
                  </div>

                  {/* Assumptions — the uplifts the simulation applies on top of
                      the estimates. These were hardcoded and invisible. */}
                  <div className="bg-white border border-gray-200 rounded-xl">
                    <button
                      onClick={() => setShowAssumptions(v => !v)}
                      className="w-full flex items-center justify-between px-5 py-3 text-left"
                    >
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Simulation assumptions</span>
                      <span className="text-xs text-gray-400">
                        {datasimConstraints ? 'customised' : 'defaults'} · {showAssumptions ? 'hide' : 'show'}
                      </span>
                    </button>
                    {showAssumptions && (
                      <div className="px-5 pb-4 space-y-3 border-t border-gray-100 pt-3">
                        {[
                          { key: 'planning_fallacy', label: 'Planning fallacy uplift', def: 0.15 },
                          { key: 'scope_creep_factor', label: 'Scope creep factor', def: 0.10 },
                        ].map(({ key, label, def }) => (
                          <div key={key} className="flex items-center justify-between gap-4">
                            <label className="text-xs text-gray-700">{label}</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number" min={0} max={100} step={1}
                                value={Math.round(((datasimConstraints?.[key] ?? def) as number) * 100)}
                                onChange={(e) => {
                                  const pct = Math.min(100, Math.max(0, Number(e.target.value) || 0));
                                  setDatasimConstraints((prev: any) => ({ ...(prev || {}), [key]: pct / 100 }));
                                }}
                                className="w-20 text-xs border border-gray-300 rounded px-2 py-1 text-right"
                              />
                              <span className="text-xs text-gray-400">%</span>
                            </div>
                          </div>
                        ))}
                        {[
                          { key: 'parkinson', label: "Parkinson's Law", def: true },
                          { key: 'student_syndrome', label: 'Student syndrome', def: true },
                          { key: 'external_deps', label: 'External dependency volatility', def: false },
                          { key: 'brooks_law', label: "Brooks' Law", def: false },
                        ].map(({ key, label, def }) => (
                          <div key={key} className="flex items-center justify-between gap-4">
                            <label className="text-xs text-gray-700">{label}</label>
                            <input
                              type="checkbox"
                              checked={Boolean(datasimConstraints?.[key] ?? def)}
                              onChange={(e) => setDatasimConstraints((prev: any) => ({ ...(prev || {}), [key]: e.target.checked }))}
                              className="h-4 w-4"
                            />
                          </div>
                        ))}
                        <div className="flex items-center justify-between pt-1">
                          <p className="text-[10px] text-gray-400 italic">Applied on top of every task estimate. Re-run the sim to use changes.</p>
                          {datasimConstraints && (
                            <button onClick={() => setDatasimConstraints(null)} className="text-[11px] text-gray-500 underline flex-shrink-0">
                              Reset to defaults
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {runningDataSim && (
                    <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto mb-4" />
                      <p className="text-sm font-medium text-gray-700">Running 10,000 simulations...</p>
                      <p className="text-xs text-gray-400 mt-1">Usually completes in a few seconds</p>
                    </div>
                  )}

                  {!runningDataSim && sim && (
                    <>
                      {/* Overall pass/fail */}
                      <div className={`rounded-xl p-4 border ${sim.overall_pass ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{sim.overall_pass ? '✅' : '⚠️'}</span>
                          <div>
                            <p className={`text-sm font-bold ${sim.overall_pass ? 'text-green-800' : 'text-red-800'}`}>
                              {sim.overall_pass ? 'Plan passes all constraints' : 'Plan has constraint violations — review before proceeding'}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Constraints applied: {sim.constraints_applied?.join(', ') || 'None'}
                            </p>
                            {sim.assumptions?.constraints && (
                              <p className="text-[11px] text-gray-500 mt-1">
                                Assumptions:{' '}
                                {sim.assumptions.constraints.map((c: any, i: number) => (
                                  <span key={c.key} className={c.is_default ? 'text-gray-500' : 'text-indigo-700 font-medium'}>
                                    {i > 0 && ' · '}
                                    {c.label} {c.type === 'factor' ? `${Math.round(c.value * 100)}%` : (c.value ? 'on' : 'off')}
                                    {!c.is_default && ' (changed)'}
                                  </span>
                                ))}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Timeline distribution */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Effort Distribution (hours)</p>
                        <div className="grid grid-cols-4 gap-3">
                          {[
                            { label: 'P10 (Best)', value: sim.simulation.p10_hours, color: 'text-green-600' },
                            { label: 'P50 (Likely)', value: sim.simulation.p50_hours, color: 'text-blue-600' },
                            { label: 'P90 (Worst)', value: sim.simulation.p90_hours, color: 'text-red-600' },
                            { label: 'Mean', value: sim.simulation.mean_hours, color: 'text-gray-700' },
                          ].map(({ label, value, color }) => (
                            <div key={label} className="text-center bg-gray-50 rounded-lg p-3">
                              <p className={`text-xl font-bold ${color}`}>{value?.toLocaleString()}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Margin + Workload checks */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white border border-gray-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Margin Check</p>
                            <CheckBadge pass={sim.margin_check.pass} />
                          </div>
                          <p className="text-xs text-gray-500">Floor: {((sim.margin_check.margin_floor || 0) * 100).toFixed(0)}%</p>
                          {sim.margin_check.p90_margin !== null && (
                            <p className="text-xs text-gray-500">P90 margin: {((sim.margin_check.p90_margin || 0) * 100).toFixed(1)}%</p>
                          )}
                          <p className="text-xs text-gray-500">P90 cost: ${(sim.margin_check.p90_cost || 0).toLocaleString()}</p>
                          <p className="text-xs text-gray-500">Price: ${(sim.margin_check.recommended_price || 0).toLocaleString()}</p>
                          {/* This check snapshots the plan's price at the moment
                              it ran. Re-pricing the plan afterwards leaves this
                              panel showing an older number than the pricing
                              panel above it — and the pass/fail badge is still
                              computed off the old one. Say so rather than
                              letting the two contradict each other silently. */}
                          {(() => {
                            const planArt = (engagement.artifacts || []).find((a: any) => a.type === 'project_plan');
                            const pricedAt = (planArt?.content as any)?.pricing?.priced_at;
                            const ranAt = simArtifact?.metadata?.ran_at;
                            if (!pricedAt || !ranAt || new Date(pricedAt) <= new Date(ranAt)) return null;
                            const current = (planArt?.content as any)?.summary?.recommended_price;
                            if (current === sim.margin_check.recommended_price) return null;
                            return (
                              <p className="text-xs text-amber-700 mt-1">
                                The plan was re-priced after this run — it is now $
                                {Number(current || 0).toLocaleString()}. Re-run DataSim to refresh this check.
                              </p>
                            );
                          })()}
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Workload Check</p>
                            <CheckBadge pass={sim.workload_check.pass} />
                          </div>
                          <p className="text-[11px] text-gray-500 mb-2">
                            Per-individual cap: {sim.workload_check.ceiling_hours} hrs/wk at 100% allocation. Roles below half allocation get a proportionally lower cap.
                          </p>
                          {Array.isArray(sim.workload_check.per_role) && sim.workload_check.per_role.length > 0 ? (
                            <div className="overflow-x-auto -mx-4 px-4">
                              <table className="w-full text-[11px]">
                                <thead>
                                  <tr className="text-gray-500 border-b border-gray-200">
                                    <th className="text-left py-1 pr-2 font-medium">Role</th>
                                    <th className="text-right py-1 pr-2 font-medium">Alloc</th>
                                    <th className="text-right py-1 pr-2 font-medium">Weekly</th>
                                    <th className="text-right py-1 pr-2 font-medium">Cap</th>
                                    <th className="text-right py-1 font-medium">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sim.workload_check.per_role.map((r: any, i: number) => (
                                    <tr key={i} className="border-b border-gray-100 last:border-0">
                                      <td className="py-1 pr-2 text-gray-800">{r.role}</td>
                                      <td className="py-1 pr-2 text-right text-gray-600">
                                        {r.allocation_pct != null ? `${r.allocation_pct}%` : '—'}
                                      </td>
                                      <td className="py-1 pr-2 text-right text-gray-700">{r.weekly_hours} hrs</td>
                                      <td className="py-1 pr-2 text-right text-gray-500">{r.ceiling_hours} hrs</td>
                                      <td className="py-1 text-right">
                                        {r.unassigned ? (
                                          <span className="text-amber-600" title="Role not in your team capacity composition. Add it under the Plan stage to apply a cap.">⚠ unassigned</span>
                                        ) : r.pass ? (
                                          <span className="text-emerald-600">✓</span>
                                        ) : (
                                          <span className="text-red-600">✗ over</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {sim.workload_check.unassigned_role_count > 0 && (
                                <p className="text-[10px] text-amber-700 mt-2 italic">
                                  {sim.workload_check.unassigned_role_count} role(s) in this plan are not in your team capacity composition. Add them on the Plan stage so DataSim can apply a per-person cap.
                                </p>
                              )}
                            </div>
                          ) : (
                            <>
                              <p className="text-xs text-gray-500">Team-aggregate weekly: {sim.workload_check.avg_weekly_hours} hrs</p>
                              <p className="text-[10px] text-amber-700 mt-1 italic">No team capacity set on this plan — falling back to team-aggregate check. Re-generate the plan with team capacity to see per-role limits.</p>
                            </>
                          )}
                          <p className="text-xs text-gray-500 mt-2">Timeline: {sim.workload_check.timeline_weeks} weeks</p>
                        </div>
                      </div>
                      <EstimateDisclaimer className="mt-1" />

                      {/* Schedule check — elapsed weeks, not effort hours.
                          Advisory: it is not part of overall_pass, because the
                          AI Council loop below is only prompted to fix margin
                          and workload. */}
                      {sim.schedule_check && sim.schedule_check.p50_weeks != null && (
                        <div className={`bg-white border rounded-xl p-5 ${sim.schedule_check.pass === false ? 'border-amber-300' : 'border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Schedule Check (elapsed weeks)</p>
                            {sim.schedule_check.pass === false ? (
                              <span className="text-[11px] font-medium text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">Advisory — runs past the plan</span>
                            ) : (
                              <CheckBadge pass={sim.schedule_check.pass} />
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            {[
                              { label: 'Planned', value: sim.schedule_check.planned_weeks, color: 'text-gray-700' },
                              { label: 'P50 (Likely)', value: sim.schedule_check.p50_weeks, color: 'text-blue-600' },
                              { label: 'P90 (Worst)', value: sim.schedule_check.p90_weeks, color: 'text-red-600' },
                            ].map(({ label, value, color }) => (
                              <div key={label} className="text-center bg-gray-50 rounded-lg p-3">
                                <p className={`text-xl font-bold ${color}`}>{value != null ? `${value}w` : '—'}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                              </div>
                            ))}
                          </div>
                          {Array.isArray(sim.schedule_check.critical_path) && sim.schedule_check.critical_path.length > 0 && (
                            <div className="mt-3">
                              <p className="text-[11px] font-medium text-gray-600 mb-1">Critical path</p>
                              {sim.schedule_check.critical_path.map((t: any, i: number) => (
                                <p key={i} className="text-[11px] text-gray-600">
                                  {t.team} — on the critical path in {Math.round((t.probability_on_critical_path || 0) * 100)}% of runs, finishing P50 {t.p50_completion_weeks}w / P90 {t.p90_completion_weeks}w
                                </p>
                              ))}
                            </div>
                          )}
                          {Array.isArray(sim.schedule_check.dependencies) && sim.schedule_check.dependencies.length > 0 && (
                            <div className="mt-3">
                              <p className="text-[11px] font-medium text-gray-600 mb-1">Dependencies derived from the plan&apos;s own schedule</p>
                              {sim.schedule_check.dependencies.map((d: any, i: number) => (
                                <p key={i} className="text-[11px] text-gray-600">
                                  {d.from_team} → {d.to_team} · {d.handoff_risk} handoff risk
                                  <span className="text-gray-400"> ({d.derived_from})</span>
                                </p>
                              ))}
                            </div>
                          )}
                          <p className="text-[10px] text-gray-400 mt-3 italic">
                            Each role runs its own tasks in sequence at its stated allocation; roles run in parallel unless the plan&apos;s week numbers say one waits on another. Advisory only — it does not block the stage.
                          </p>
                        </div>
                      )}

                      {/* Where each range came from — the point of the whole
                          pressure test is that a P90 built on a cited benchmark
                          and one built on a multiplier are not the same claim. */}
                      {sim.estimate_basis && Array.isArray(sim.estimate_basis.variables) && sim.estimate_basis.variables.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">What the ranges rest on</p>
                            <p className="text-[11px] text-gray-500">
                              {sim.estimate_basis.consultant_override > 0 && `${sim.estimate_basis.consultant_override} yours · `}
                              {sim.estimate_basis.researched} researched · {sim.estimate_basis.flagged_unknown} flagged unknown · {sim.estimate_basis.estimator_confidence} estimator judgement
                            </p>
                          </div>
                          {sim.estimate_basis.outside_researched_band > 0 && (
                            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                              {sim.estimate_basis.outside_researched_band} estimate(s) fall outside the researched band — the planned hours disagree with the benchmark. The band was kept; review these before pricing.
                            </p>
                          )}
                          {Object.keys(rangeOverrides).length > 0 && (
                            <div className="flex items-center justify-between text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 mb-3">
                              <span>{Object.keys(rangeOverrides).length} range(s) edited — re-run the sim to apply them.</span>
                              <button onClick={() => setRangeOverrides({})} className="underline flex-shrink-0">Discard edits</button>
                            </div>
                          )}
                          <div className="overflow-x-auto -mx-5 px-5">
                            <table className="w-full text-[11px]">
                              <thead>
                                <tr className="text-gray-500 border-b border-gray-200">
                                  <th className="text-left py-1 pr-2 font-medium">Task</th>
                                  <th className="text-right py-1 pr-2 font-medium">Planned</th>
                                  <th className="text-right py-1 pr-2 font-medium">Range (O / M / P)</th>
                                  <th className="text-left py-1 pl-3 font-medium">Basis</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sim.estimate_basis.variables.map((v: any, i: number) => (
                                  <tr key={i} className="border-b border-gray-100 last:border-0 align-top">
                                    <td className="py-1 pr-2 text-gray-800">
                                      {v.task}
                                      {v.workstream && <span className="text-gray-400"> · {v.workstream}</span>}
                                    </td>
                                    <td className="py-1 pr-2 text-right text-gray-600">{v.hours}h</td>
                                    <td className="py-1 pr-2 text-right text-gray-700 whitespace-nowrap">
                                      {editingRange === v.key ? (
                                        <div className="flex items-center justify-end gap-1">
                                          {['optimistic', 'most_likely', 'pessimistic'].map((f) => (
                                            <input
                                              key={f}
                                              type="number" min={0}
                                              defaultValue={rangeOverrides[v.key]?.[f] ?? v[f]}
                                              onChange={(e) => setRangeOverrides((prev) => ({
                                                ...prev,
                                                [v.key]: {
                                                  optimistic: v.optimistic, most_likely: v.most_likely, pessimistic: v.pessimistic,
                                                  ...(prev[v.key] || {}),
                                                  [f]: Number(e.target.value),
                                                },
                                              }))}
                                              className="w-14 border border-gray-300 rounded px-1 py-0.5 text-right text-[11px]"
                                            />
                                          ))}
                                          <button onClick={() => setEditingRange(null)} className="text-[10px] text-blue-600 underline ml-1">done</button>
                                        </div>
                                      ) : (
                                        <button onClick={() => setEditingRange(v.key)} className="hover:underline" title="Correct this range by hand">
                                          {rangeOverrides[v.key]
                                            ? `${rangeOverrides[v.key].optimistic} / ${rangeOverrides[v.key].most_likely} / ${rangeOverrides[v.key].pessimistic}`
                                            : `${v.optimistic} / ${v.most_likely} / ${v.pessimistic}`}
                                        </button>
                                      )}
                                      {editingRange === v.key && (
                                        <input
                                          type="text"
                                          placeholder="Why? (shown as the rationale)"
                                          defaultValue={rangeOverrides[v.key]?.note || ''}
                                          onChange={(e) => setRangeOverrides((prev) => ({ ...prev, [v.key]: { ...(prev[v.key] || {}), note: e.target.value } }))}
                                          className="mt-1 w-full border border-gray-300 rounded px-1 py-0.5 text-[10px] text-left"
                                        />
                                      )}
                                    </td>
                                    <td className="py-1 pl-3">
                                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        v.basis === 'consultant_override' ? 'bg-indigo-100 text-indigo-800'
                                        : v.basis === 'researched_cited' ? 'bg-emerald-100 text-emerald-800'
                                        : v.basis === 'researched_uncited' ? 'bg-teal-100 text-teal-800'
                                        : v.basis === 'flagged_unknown' ? 'bg-amber-100 text-amber-800'
                                        : 'bg-gray-100 text-gray-600'}`}>
                                        {v.basis === 'consultant_override' ? 'your correction'
                                          : v.basis === 'researched_cited' ? `researched · ${v.source_count} sources`
                                          : v.basis === 'researched_uncited' ? 'researched · no sources'
                                          : v.basis === 'flagged_unknown' ? 'flagged unknown'
                                          : 'estimator judgement'}
                                      </span>
                                      {v.rationale && <p className="text-[10px] text-gray-500 mt-0.5">{v.rationale}</p>}
                                      {v.caveat && <p className="text-[10px] text-amber-700 mt-0.5">Caveat: {v.caveat}</p>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Top risks */}
                      {sim.top_risks?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Top Risk Drivers</p>
                          <div className="space-y-2">
                            {sim.top_risks.map((r: any, i: number) => (
                              <div key={i} className="flex items-center gap-3">
                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                  <div
                                    className="bg-orange-500 h-1.5 rounded-full"
                                    style={{ width: `${Math.abs(r.spearman_r) * 100}%` }}
                                  />
                                </div>
                                <p className="text-xs text-gray-600 flex-shrink-0 w-48 truncate" title={r.task}>{r.task}</p>
                                {r.basis && (
                                  <p className={`text-[10px] flex-shrink-0 ${r.basis.startsWith('researched') ? 'text-emerald-700' : r.basis === 'flagged_unknown' ? 'text-amber-700' : 'text-gray-400'}`}>
                                    {r.basis.startsWith('researched') ? 'researched' : r.basis === 'flagged_unknown' ? 'unknown' : 'judgement'}
                                  </p>
                                )}
                                <p className="text-xs text-gray-400 flex-shrink-0">{(Math.abs(r.spearman_r) * 100).toFixed(0)}%</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Council — shown whenever DataSim fails, regardless of approval status */}
                      {!sim.overall_pass && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-amber-900">AI Council Review</p>
                              <p className="text-xs text-amber-700 mt-0.5">Get specific recommendations to fix the constraint violations</p>
                            </div>
                            {!councilResult && (
                              <button
                                onClick={fetchCouncilRecommendations}
                                disabled={fetchingCouncil}
                                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50 flex-shrink-0"
                              >
                                {fetchingCouncil ? (
                                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Analyzing...</>
                                ) : (
                                  <>🧠 Get Recommendations</>
                                )}
                              </button>
                            )}
                          </div>

                          {councilResult && (
                            <>
                              <p className="text-sm text-amber-800 bg-amber-100 rounded-lg px-4 py-3">{councilResult.summary}</p>
                              <div className="space-y-2">
                                {councilResult.recommendations.map((rec: any) => (
                                  <div
                                    key={rec.id}
                                    onClick={() => setAcceptedRecs(prev => {
                                      const next = new Set(prev);
                                      next.has(rec.id) ? next.delete(rec.id) : next.add(rec.id);
                                      return next;
                                    })}
                                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                      acceptedRecs.has(rec.id)
                                        ? 'bg-white border-amber-400'
                                        : 'bg-amber-50 border-transparent opacity-60'
                                    }`}
                                  >
                                    <div className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 border-2 flex items-center justify-center ${
                                      acceptedRecs.has(rec.id) ? 'bg-amber-500 border-amber-500' : 'border-gray-300'
                                    }`}>
                                      {acceptedRecs.has(rec.id) && <div className="w-2 h-2 bg-white rounded-sm" />}
                                    </div>
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 mb-0.5">
                                        <p className="text-sm font-semibold text-gray-900">{rec.title}</p>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                          rec.effort === 'low' ? 'bg-green-100 text-green-700' :
                                          rec.effort === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                          'bg-red-100 text-red-700'
                                        }`}>{rec.effort} effort</span>
                                      </div>
                                      <p className="text-xs text-gray-600">{rec.description}</p>
                                      <p className="text-xs text-amber-700 mt-1">→ {rec.impact}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="flex items-center justify-between pt-1">
                                <button
                                  onClick={applyRevision}
                                  disabled={applyingRevision || fetchingCouncil || acceptedRecs.size === 0}
                                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                                >
                                  {applyingRevision ? (
                                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Applying & Re-running...</>
                                  ) : (
                                    <>Apply in project & Re-run DataSim ({acceptedRecs.size} selected)</>
                                  )}
                                </button>
                                <button
                                  onClick={() => { setCouncilResult(null); setAcceptedRecs(new Set()); }}
                                  className="text-sm text-gray-500 hover:text-gray-700"
                                >
                                  Dismiss
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Approve */}
                      {simArtifact?.status !== 'approved' ? (
                        <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                          <p className="text-sm text-gray-600 mr-4">
                            {sim.overall_pass ? 'Results look good. Approve to proceed to SOW.' : 'Constraints flagged — consider adjusting the plan or accept risk.'}
                          </p>
                          <button
                            onClick={() => approveSimAndProceed(simArtifact!.id)}
                            disabled={approvingSim || fetchingCouncil || applyingRevision}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex-shrink-0"
                          >
                            {approvingSim ? (
                              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
                            ) : (
                              <><CheckCircle className="w-4 h-4" /> Approve & Proceed</>
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <p className="text-sm font-medium text-green-800">DataSim approved</p>
                          </div>
                          <button
                            onClick={() => engagement.stage === 'datasim' ? advanceStage() : setViewStage('sow')}
                            disabled={advancing}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                          >
                            {advancing ? 'Moving...' : 'Proceed to SOW'}
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {!runningDataSim && !sim && (
                    <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
                      <span className="text-4xl mb-3 block">🎲</span>
                      <p className="text-sm text-gray-500">Click "Run DataSim" to pressure-test your project plan with Monte Carlo simulation.</p>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (viewStage ?? engagement.stage) === 'sow' ? (
            /* ── SOW Stage UI ── */
            (() => {
              const sowArtifact = (engagement.artifacts || []).find(
                a => a.type === 'sow_doc'
              );
              const sow = sowArtifact?.content as any;
              return (
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-gray-900">SOW Generator</h3>
                        {sowArtifact?.status === 'approved' && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Approved
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        AI drafts a complete Statement of Work. Human review required before sharing with client.
                      </p>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0 ml-4">
                      <button
                        onClick={generateSow}
                        disabled={generatingSow || !!stageBlockReason('sow')}
                        title={stageBlockReason('sow') || ''}
                        className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FileText className="w-4 h-4" />
                        {generatingSow ? 'Generating...' : sowArtifact ? 'Regenerate SOW' : 'Generate SOW'}
                      </button>
                      {stageBlockReason('sow') && (
                        <p className="text-xs text-amber-600 mt-1.5 text-right max-w-[16rem]">{stageBlockReason('sow')}</p>
                      )}
                    </div>
                  </div>

                  {generatingSow && (
                    <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-700 mx-auto mb-4" />
                      <p className="text-sm font-medium text-gray-700">Drafting Statement of Work...</p>
                      <p className="text-xs text-gray-400 mt-1">Takes 30–60 seconds</p>
                    </div>
                  )}

                  {!generatingSow && sowArtifact && (
                    <>
                      {/* SOW preview */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <span className="text-2xl">📄</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{sowArtifact.metadata?.original_name || 'SOW.docx'}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {sowArtifact.metadata?.file_size ? `${(sowArtifact.metadata.file_size / 1024).toFixed(0)} KB` : ''} · Status: {sowArtifact.status}
                            </p>
                            {/* Word count, because "make it 50% shorter" is a
                                number the reader should be able to see. */}
                            {sowArtifact.metadata?.length_report && (
                              <p className="text-[11px] mt-0.5">
                                <span className={sowArtifact.metadata.length_report.within_budget ? 'text-emerald-700' : 'text-amber-700'}>
                                  {sowArtifact.metadata.length_report.total_words} words
                                </span>
                                <span className="text-gray-400"> of {sowArtifact.metadata.length_report.budget_words} budgeted</span>
                                {sowArtifact.metadata.length_report.compared_to_previous && (
                                  <span className="text-gray-500">
                                    {' · '}
                                    {sowArtifact.metadata.length_report.compared_to_previous.pct_reduction >= 0
                                      ? `${sowArtifact.metadata.length_report.compared_to_previous.pct_reduction}% shorter than the previous draft`
                                      : `${Math.abs(sowArtifact.metadata.length_report.compared_to_previous.pct_reduction)}% longer than the previous draft`}
                                  </span>
                                )}
                                {!sowArtifact.metadata.length_report.within_budget && (
                                  <span className="text-amber-700"> · long in: {sowArtifact.metadata.length_report.over_budget_sections.join(', ')}</span>
                                )}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={async () => { setDownloadingStageFile(true); await downloadArtifact(sowArtifact.id); setDownloadingStageFile(false); }}
                            disabled={downloadingStageFile}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                          >
                            {downloadingStageFile
                              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Downloading...</>
                              : <><Download className="w-4 h-4" /> Download Word</>
                            }
                          </button>
                        </div>

                        {/* SOW content preview */}
                        {sow?.executive_summary && (
                          <div className="border-t border-gray-100 pt-4 space-y-3">
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Executive Summary</p>
                              <p className="text-sm text-gray-700">{sow.executive_summary}</p>
                            </div>
                            {sow.out_of_scope?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Out of Scope</p>
                                {sow.out_of_scope.map((s: string, i: number) => (
                                  <p key={i} className="text-xs text-gray-600">• {s}</p>
                                ))}
                              </div>
                            )}
                            {sow.assumptions?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Key Assumptions</p>
                                {sow.assumptions.slice(0, 3).map((a: string, i: number) => (
                                  <p key={i} className="text-xs text-gray-600">• {a}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <p className="text-xs text-amber-800">⚠️ Review the downloaded Word document carefully before sharing with the client. Do not share the draft version.</p>
                      </div>

                      {sowArtifact.status !== 'approved' ? (
                        <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                          <p className="text-sm text-gray-600 mr-4">Downloaded and reviewed? Approve to mark deal as Won.</p>
                          <button
                            onClick={() => approveSow(sowArtifact.id)}
                            disabled={approvingSow}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex-shrink-0"
                          >
                            {approvingSow ? (
                              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
                            ) : (
                              <><CheckCircle className="w-4 h-4" /> Approve SOW</>
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <p className="text-sm font-medium text-green-800">{engagement.stage === 'won' ? 'SOW approved & marked as Won' : 'SOW approved — ready to mark as Won'}</p>
                          </div>
                          <button
                            onClick={() => engagement.stage === 'sow' ? advanceStage() : setViewStage('won')}
                            disabled={advancing}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                          >
                            {advancing ? (
                              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
                            ) : engagement.stage === 'sow' ? (
                              <>🏆 Mark as Won & Proceed</>
                            ) : (
                              <>Proceed to PM <ArrowRight className="w-4 h-4" /></>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {!generatingSow && !sowArtifact && (
                    <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
                      <span className="text-4xl mb-3 block">📄</span>
                      <p className="text-sm text-gray-500">Click "Generate SOW" to draft your Statement of Work.</p>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (viewStage ?? engagement.stage) === 'won' ? (
            /* ── PM Handoff Stage UI ── */
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center gap-4">
                <span className="text-3xl">🏆</span>
                <div>
                  <h3 className="text-base font-semibold text-emerald-900">Deal Won!</h3>
                  <p className="text-sm text-emerald-700 mt-0.5">Push the project plan to Project Management to kick off delivery.</p>
                </div>
              </div>

              {engagement.metadata?.pm_project_id || handoffResult ? (
                <div className="bg-white border border-gray-200 rounded-xl p-6 text-center space-y-4">
                  <div className="text-5xl">✅</div>
                  <div>
                    <p className="text-base font-semibold text-gray-900">Handoff Complete!</p>
                    {handoffResult ? (
                      <p className="text-sm text-gray-500 mt-1">
                        {handoffResult.workstreams_created} workstreams · {handoffResult.tasks_created} tasks created in PM
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 mt-1">Project already pushed to Project Management</p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const pmId = engagement.metadata?.pm_project_id || handoffResult?.pm_project_id;
                      navigate(`/app/project-management${pmId ? `?projectId=${pmId}&tab=board` : ''}`);
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    View in Project Management <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">What will be created in PM:</h4>
                  {(() => {
                    const planArtifact = (engagement.artifacts || []).find(a => a.type === 'project_plan' && a.status === 'approved');
                    const plan = planArtifact?.content as any;
                    return plan ? (
                      <div className="space-y-2 mb-5">
                        {(plan.workstreams || []).map((ws: any, i: number) => (
                          <div key={i} className="flex items-center justify-between text-sm border border-gray-100 rounded-lg px-3 py-2">
                            <span className="font-medium text-gray-800">{ws.name}</span>
                            <span className="text-xs text-gray-500">{ws.tasks?.length || 0} tasks</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-sm text-gray-400 mb-4">No project plan found.</p>;
                  })()}
                  <button
                    onClick={runPmHandoff}
                    disabled={handingOff}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {handingOff ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Pushing to PM...</>
                    ) : (
                      <><ArrowRight className="w-4 h-4" /> Push to Project Management</>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* ── Other stages: Coming Soon ── */
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {STAGES.find(s => s.key === engagement.stage)?.label || 'Stage'} — Coming Soon
              </h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
                {STAGES.find(s => s.key === engagement.stage)?.description}
              </p>

              {engagement.smart_start_session_id && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <ChevronRight className="w-4 h-4 text-blue-600" />
                    <p className="text-sm font-medium text-blue-800">Smart Start Session Linked</p>
                  </div>
                  <p className="text-xs text-blue-600">
                    Session #{engagement.smart_start_session_id} is linked to this engagement.
                  </p>
                  <button
                    onClick={() => navigate(`/app/smart-start/${engagement.smart_start_session_id}`)}
                    className="mt-3 text-xs font-medium text-blue-700 hover:text-blue-900 underline"
                  >
                    Open in Smart Start →
                  </button>
                </div>
              )}

              {!engagement.smart_start_session_id && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    <p className="text-sm text-yellow-800">No Smart Start session linked yet</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Right: Artifacts Panel ── */}
        <div className="col-span-3">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Artifacts ({activeArtifacts.length})
            </h3>

            {activeArtifacts.length === 0 ? (
              <div className="text-center py-6 text-gray-400">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No artifacts yet</p>
                <p className="text-xs mt-0.5">Generated outputs appear here</p>
              </div>
            ) : (
              <div className="space-y-2">
                {activeArtifacts.map(artifact => (
                  <div key={artifact.id} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-base flex-shrink-0">
                        {ARTIFACT_ICONS[artifact.type] || '📄'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-medium text-gray-800 truncate">
                            {ARTIFACT_LABELS[artifact.type] || artifact.type}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          {!['transcript', 'source_doc'].includes(artifact.type) && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${STATUS_COLORS[artifact.status]}`}>
                              {artifact.status}
                            </span>
                          )}
                          {artifact.created_by_type === 'user' && !['transcript', 'source_doc'].includes(artifact.type) && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700" title="Uploaded by a user, not AI-generated">
                              Uploaded
                            </span>
                          )}
                          {artifact.prerequisites_bypassed && (
                            <span title="Prerequisites were bypassed">
                              <Unlock className="w-3 h-3 text-amber-500" />
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1.5 mt-2">
                          {artifact.file_ref && (
                            <button
                              onClick={async () => {
                                setDownloadingId(artifact.id);
                                await downloadArtifact(artifact.id);
                                setDownloadingId(null);
                              }}
                              disabled={downloadingId === artifact.id}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                            >
                              {downloadingId === artifact.id
                                ? <><div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin" /> Downloading...</>
                                : <><Download className="w-3 h-3" /> Download</>
                              }
                            </button>
                          )}
                          {artifact.status === 'draft' && !['transcript', 'source_doc'].includes(artifact.type) && (
                            <>
                              <button
                                onClick={() => updateArtifactStatus(artifact.id, 'approved')}
                                disabled={approvingArtifactId === artifact.id}
                                className="text-xs text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                              >
                                {approvingArtifactId === artifact.id ? 'Approving...' : 'Approve'}
                              </button>
                              <span className="text-gray-300">|</span>
                              <button
                                onClick={() => updateArtifactStatus(artifact.id, 'in_review')}
                                disabled={approvingArtifactId === artifact.id}
                                className="text-xs text-yellow-600 hover:text-yellow-800 disabled:opacity-50"
                              >
                                Review
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>

          {/* Engagement Meta */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 mt-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Details</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Created</dt>
                <dd className="text-gray-800 font-medium">
                  {new Date(engagement.created_at).toLocaleDateString()}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Updated</dt>
                <dd className="text-gray-800 font-medium">
                  {new Date(engagement.updated_at).toLocaleDateString()}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Margin Floor</dt>
                <dd className="text-gray-800 font-medium">
                  {(Number(engagement.margin_floor_override) * 100).toFixed(0)}%
                </dd>
              </div>
              {engagement.owner && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Owner</dt>
                  <dd className="text-gray-800 font-medium truncate max-w-[120px]">
                    {engagement.owner.name}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>

    {/* DataSim Override Confirmation Modal */}

    {datasimOverrideModal.open && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={() => setDatasimOverrideModal(prev => ({ ...prev, open: false }))} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Override DataSim Constraints?</h3>
              <p className="text-sm text-gray-500 mt-1">
                This engagement failed one or more constraints (margin floor or workload ceiling). Proceeding to SOW without fixing these violations may put the engagement at risk.
              </p>
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-5">
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Constraint Violations</p>
            <p className="text-sm text-red-800">Margin floor or workload ceiling not met. Review AI Council recommendations before proceeding.</p>
          </div>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setDatasimOverrideModal(prev => ({ ...prev, open: false }))}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              Cancel — Go Back
            </button>
            <button
              onClick={async () => {
                setDatasimOverrideModal(prev => ({ ...prev, open: false }));
                try { await datasimOverrideModal.onConfirm(); } catch { toast.error('Failed to proceed'); }
              }}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              Yes, Override & Proceed to SOW
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default BDEngagementDetail;
